import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BorderBeam } from 'border-beam';
import {
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Minus,
    Pencil,
    Play,
    Plus,
    Sparkles,
    Upload,
    Video,
    Wand2,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { NodeShell } from './NodeShell';
import { CircleActionButton, SvgDuplicate, SvgRemove, SvgAgent} from './ProductNode';
import { SegmentedChoice } from '../../../../components/design-system/common/SegmentedChoice';
import { ChoiceSlider } from '../../../../components/design-system/common/ChoiceSlider';
import { CsRecipe, CsRecipeInput } from '../../../../common/models/content-studio.model';
import { resolveCreditCost } from '../recipe-pricing';

import { CameraControl3D } from './CameraControl3D';
import { ProductPoseWidget } from './ProductPoseWidget';
import { CloseUpCropWidget } from './CloseUpCropWidget';
import { Furniture3DStudioWidget } from './Furniture3DStudioWidget';
import { MoodboardComposer } from './MoodboardComposer';
import { ProductNodeProduct } from './ProductNode';
import { Surface3DStudioWidget } from './Surface3DStudioWidget';
import { useSeedI18n } from '../hooks/use-seed-i18n.hook';
import {
    CsAppliesTo,
    resolveRecipeFlags,
    resolveMissingInputs,
    resolveVisibleInputs,
    surfaceShotName,
} from '../recipe-schema';
import {
    getOptionPreviewUrl,
    getOptionPreviewVideoUrl,
    getOptionPreviewPosterUrl,
} from '../option-previews';

// Illustrations
import { FRAMING_ILLUSTRATIONS, FRAMING_ILLUSTRATIONS_SURFACE } from './recipe-illustrations/FramingIllustrations';
import { BackdropSwatch, BACKDROP_SWATCHES } from './recipe-illustrations/BackdropIllustrations';
import { LIGHTING_ILLUSTRATIONS } from './recipe-illustrations/LightingIllustrations';
import { LIGHTING_STYLE_ILLUSTRATIONS } from './recipe-illustrations/LightingStyleIllustrations';
import { WALL_MATERIAL_TEXTURES, FLOOR_MATERIAL_TEXTURES, MaterialTextureSwatch } from './recipe-illustrations/MaterialTextureIllustrations';
import { AspectRatioVisual } from './recipe-illustrations/AspectRatioPicker';
import { AspectRatioControl } from './AspectRatioControl';
import { PlaceableProduct, PlaceInRoomSection, RoomApi } from './PlaceInRoomSection';
import { SHADOW_ILLUSTRATIONS } from './recipe-illustrations/ShadowIllustrations';
import { DOF_ILLUSTRATIONS } from './recipe-illustrations/DepthOfFieldIllustrations';
import { CAMERA_HEIGHT_ILLUSTRATIONS } from './recipe-illustrations/CameraHeightIllustrations';
import {
    HERO_ROTATION_ILLUSTRATIONS,
    HERO_ROTATION_ILLUSTRATIONS_SURFACE,
} from './recipe-illustrations/HeroRotationIllustrations';

/** One shared empty array — a new [] per render is a changed prop. */
const EMPTY_PRODUCTS: ProductNodeProduct[] = [];

/**
 * The round 3D-studio toggle that sits in the top toolbar. The furniture and
 * the surface branches drew it identically, so it lives here once.
 */
const StudioToggleButton: React.FC<{ on: boolean; onClick: () => void; title: string }> = ({ on, onClick, title }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-pressed={on}
        className={cn(
            'flex size-[34px] shrink-0 items-center justify-center rounded-full border p-0 transition-colors',
            on
                ? 'border-foreground bg-foreground text-background hover:bg-foreground/90'
                : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
    >
        <Video className="size-[17px]" />
    </button>
);

/**
 * The quiet explanatory note a few recipe branches drop under their toolbar
 * ("no room is built", "AI reads your framing"). Three copies of the same
 * markup before it moved here.
 */
const NoteBox: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
    <div className="flex items-start gap-1.5 rounded-md border bg-muted/40 px-2.5 py-2 text-[11px] leading-snug text-muted-foreground">
        {icon && <span className="mt-px shrink-0">{icon}</span>}
        <span>{children}</span>
    </div>
);

/**
 * A two-state chooser pill. Compose (Studio / Moodboard), studio-3d's Scene
 * (Studio / Room) and its Use AI switch are all the same control.
 */
const PillChoice: React.FC<{
    active: boolean;
    onClick: () => void;
    title?: string;
    className?: string;
    children: React.ReactNode;
}> = ({ active, onClick, title, className, children }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-pressed={active}
        className={cn(
            'rounded-lg border px-3 py-1 text-xs font-medium transition-colors',
            active
                ? 'border-foreground bg-accent text-foreground'
                : 'border-border bg-background text-muted-foreground hover:bg-muted',
            className,
        )}
    >
        {children}
    </button>
);

/**
 * The node's hover actions. The folded card and the open one carry the same
 * three, so the row is built once and handed to NodeShell twice.
 */
const NodeActions: React.FC<{
    onDuplicate?: () => void;
    onTargetInChat?: () => void;
    onRemove?: () => void;
}> = ({ onDuplicate, onTargetInChat, onRemove }) => {
    const { t } = useTranslation();
    return (
        <div className="flex gap-1.5">
            {onDuplicate && (
                <CircleActionButton title={t('CONTENT_STUDIO.ACTION_DUPLICATE')} icon={<SvgDuplicate />} onClick={onDuplicate} />
            )}
            {onTargetInChat && (
                <CircleActionButton
                    title={t('CONTENT_STUDIO.ACTION_ASK_AGENT', 'Ask the agent about this') as string}
                    icon={<SvgAgent />}
                    onClick={onTargetInChat}
                />
            )}
            {onRemove && (
                <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REMOVE')} icon={<SvgRemove />} onClick={onRemove} destructive />
            )}
        </div>
    );
};

/**
 * Generate — drawn once, rendered on both the folded card and the open one so
 * the two can never drift on cost, blocked state or wording.
 */
const GenerateButton: React.FC<{
    generating?: boolean;
    canAfford: boolean;
    blocked: boolean;
    cost: number;
    showIcon?: boolean;
    onClick: (e: React.MouseEvent) => void;
}> = ({ generating, canAfford, blocked, cost, showIcon, onClick }) => {
    const { t } = useTranslation();
    const inert = !canAfford || blocked;
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2.5 text-xs font-medium transition-colors',
                inert ? 'text-muted-foreground/60' : 'text-foreground',
                generating || inert ? 'cursor-not-allowed' : 'cursor-pointer hover:border-foreground/30 hover:bg-muted',
                blocked && !generating && 'opacity-55',
            )}
        >
            {generating ? (
                <>
                    <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                    {t('CONTENT_STUDIO.GENERATING_BTN', 'Generating…')}
                </>
            ) : (
                <>
                    {showIcon && <Play className="size-4" />}
                    {t('CONTENT_STUDIO.GENERATE', 'Generate')} · {cost} cr
                </>
            )}
        </button>
    );
};

// The full set of ratios the 3D studio supports. The recipe seed defines the
// same list, but a stale DB recipe can be missing some (e.g. 9:16) — merge these
// in so the toolbar always offers every ratio the studio can capture.
const CANONICAL_ASPECTS: Array<{ value: string; label: string }> = [
    { value: '1:1', label: 'Square (1:1)' },
    { value: '4:5', label: 'Portrait (4:5)' },
    { value: '3:4', label: 'Portrait (3:4)' },
    { value: '9:16', label: 'Story (9:16)' },
    { value: '16:9', label: 'Landscape (16:9)' },
];
const withCanonicalAspects = (opts?: Array<{ value: string; label?: string }>): Array<{ value: string; label?: string }> => {
    const seen = new Set((opts || []).map((o) => String(o.value)));
    return [...(opts || []), ...CANONICAL_ASPECTS.filter((c) => !seen.has(c.value))];
};

/**
 * Horizontal scroller for option cards — no visible scrollbar. Shows a soft
 * gradient fade + a circular prev/next button on each side, only when there's
 * more content to scroll that way.
 */
const HorizontalScroller: React.FC<{ children: React.ReactNode; scrollToIndex?: number; itemWidth?: number | 'auto' }> = ({ children, scrollToIndex, itemWidth = 96 }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);
    const update = React.useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 2);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    }, []);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        update();
        el.addEventListener('scroll', update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
    }, [update, children]);
    // On mount, bring the selected card into view (so reopening a section
    // doesn't reset to the first item). Small left offset leaves room for the
    // left button/fade.
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof scrollToIndex !== 'number' || scrollToIndex < 0) return;
        const child = el.children[scrollToIndex] as HTMLElement | undefined;
        if (!child) return;
        const cRect = child.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        el.scrollLeft += (cRect.left - elRect.left) - 40;
        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Click-and-drag to pan the row (grab cursor). A real drag suppresses the
    // click that follows, so panning never accidentally selects a card.
    const [dragging, setDragging] = useState(false);
    const onMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        const el = ref.current;
        if (!el) return;
        e.stopPropagation(); // don't let the node start dragging
        const startX = e.clientX;
        const startScroll = el.scrollLeft;
        let moved = false;
        const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            if (Math.abs(dx) > 3) moved = true;
            if (moved) { el.scrollLeft = startScroll - dx; ev.preventDefault(); }
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp, true);
            setDragging(false);
            if (moved) {
                // Swallow the click that fires after the drag release so it
                // doesn't select the card under the cursor.
                const swallow = (ce: MouseEvent) => {
                    ce.stopPropagation();
                    ce.preventDefault();
                    document.removeEventListener('click', swallow, true);
                };
                document.addEventListener('click', swallow, true);
                setTimeout(() => document.removeEventListener('click', swallow, true), 300);
            }
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp, true);
        setDragging(true);
    };
    // Fade the edges with a MASK on the scroll container itself (fades the
    // cards + their borders uniformly to transparent), rather than layering a
    // gradient Box on top — an overlay only partially covers a peeking card's
    // crisp border, leaving a thin visible line.
    const FADE = 36;
    const maskImage = canLeft && canRight
        ? `linear-gradient(to right, transparent 0, #000 ${FADE}px, #000 calc(100% - ${FADE}px), transparent 100%)`
        : canRight
        ? `linear-gradient(to right, #000 calc(100% - ${FADE}px), transparent 100%)`
        : canLeft
        ? `linear-gradient(to right, transparent 0, #000 ${FADE}px)`
        : 'none';
    // The scroller pans in physical scrollLeft space, so the edge buttons stay
    // on physical left/right rather than logical start/end.
    const Arrow: React.FC<{ side: 'left' | 'right' }> = ({ side }) => (
        <button
            type="button"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); ref.current?.scrollBy({ left: side === 'left' ? -180 : 180, behavior: 'smooth' }); }}
            aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
            className={cn(
                'absolute top-[calc(50%-4px)] z-[3] flex size-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background p-0 text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground',
                side === 'left' ? 'left-0.5' : 'right-0.5',
            )}
        >
            {side === 'left' ? <ChevronLeft className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
    );
    return (
        <div className="relative">
            {canLeft && <Arrow side="left" />}
            <div
                ref={ref}
                onMouseDown={onMouseDown}
                className={cn(
                    // overflow-x:auto forces vertical clipping too, so pad the top a
                    // little to leave room for a selected item's corner check badge.
                    'flex gap-1.5 overflow-x-auto py-1.5 [scrollbar-width:none] [&>*]:flex-[0_0_auto] [&::-webkit-scrollbar]:hidden',
                    itemWidth !== 'auto' && '[&>*]:w-[var(--cs-item-w)]',
                    dragging ? 'cursor-grabbing select-none' : 'cursor-grab',
                )}
                style={{
                    maskImage,
                    WebkitMaskImage: maskImage,
                    ...(itemWidth === 'auto' ? {} : { ['--cs-item-w' as any]: `${itemWidth}px` }),
                }}
            >
                {children}
            </div>
            {canRight && <Arrow side="right" />}
        </div>
    );
};

interface RecipeNodeProps {
    nodeId: string;
    x: number;
    y: number;
    recipe: CsRecipe;
    /** The lifestyle-scene recipe def — used to price Room Scene · Lifestyle (3D
     *  off) correctly, since that mode submits under lifestyle-scene. */
    lifestyleSceneRecipe?: CsRecipe;
    inputs: Record<string, any>;
    onInputChange: (key: string, value: any) => void;
    selected?: boolean;
    zoom: number;
    onDrag: (deltaX: number, deltaY: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onGenerate: () => void;
    onDuplicate?: () => void;
    onRemove?: () => void;
    /**
     * Point the chat at this card without composing anything.
     *
     * Distinct from `onAskAgent`, which hands over a written request: this one
     * only says WHICH node the next message is about, and the vendor supplies
     * the words. Both exist because "set this up for me" and "fill the prompt
     * on this one" are different asks.
     */
    onTargetInChat?: () => void;
    generating?: boolean;
    creditBalance?: number | null;
    onUploadStyleRef?: (file: File) => Promise<string>;  // returns URL
    /** From Reference: detect replaceable objects in the uploaded style image. */
    onDetectReferenceObjects?: (imageUrl: string) => Promise<Array<{ label: string; confidence: number; areaPct: number; bbox: number[] }>>;
    /**
     * Product application types ("floor", "wall", …) used to filter the
     * Surface input's options so a floor-only tile doesn't offer Wall.
     */
    productApplicationTypes?: string[];
    /**
     * Linked product's visualization.type ('surface' | 'model3d' | 'image_only').
     * Used to hide inputs / option values that only apply to certain product
     * families — e.g. `surface` / `environment` fields are irrelevant to
     * furniture; marble-slab backdrops make no sense under a sofa.
     */
    productVisualizationType?: string;
    /**
     * GLB/GLTF URL for the linked furniture product — drives the inline
     * 3D rotation widget on the Product Photography recipe when the
     * product is model3d. Undefined for surface / image-only products.
     */
    productModelPath?: string;
    /** Place in a room: the room — the image this recipe was chained off. */
    placeRoomUrl?: string;
    /** Place in a room: the vendor's furniture, to choose the piece from. */
    placeProducts?: PlaceableProduct[];
    /** Place in a room: analyse the chosen room into a Space, and read its scene. */
    roomApi?: RoomApi;
    /** The upstream product node's products — the pieces a moodboard is built from. */
    boardProducts?: ProductNodeProduct[];
    /** Cut the subject out of a moodboard image (paid, so it runs server-side). */
    onRemoveBackground?: (imageUrl: string) => Promise<string>;
    /** Open the catalogue picker so more products join this node's product node. */
    onAddBoardProducts?: () => void;
    /** Product UUID — used to fetch the DECRYPTED model for the pose widget. */
    productUuid?: string;
    /**
     * Pose the 3D studio to this named angle and capture it, with nobody
     * opening the dialog. Set by the page when a plan asks for an angle.
     */
    headlessCaptureAngle?: string | null;
    /**
     * A measured camera to pose to, for this capture only.
     *
     * Deliberately a prop rather than read from `inputs.cameraView`: the input
     * persists on the node, and a persistent value cannot mean "capture now" —
     * used that way it held the studio dialog open with no way to close it.
     */
    headlessCaptureCameraView?: string | null;
    /**
     * Hand this card over to the chat.
     *
     * "Use AI" used to flip a hidden flag and let a vision director choose the
     * environment, style and lighting invisibly — which is why results felt
     * generic and unfixable at once. Handing the card to the agent puts the
     * same intelligence somewhere the vendor can read it and argue with it.
     */
    onAskAgent?: (request: string) => void;
    /**
     * Measure the reference's camera and pose the studio to it.
     *
     * The measurement has always existed, but only the agent could ask for it
     * — a vendor working the card had to eyeball the angle by hand, which is
     * the drift this whole pipeline was built to remove. Resolves null on
     * success, or a sentence explaining why not.
     */
    onMatchReferenceCamera?: (imageUrl: string) => Promise<string | null>;
    /** Called when that capture finishes, so the request can be cleared. */
    onHeadlessCaptureDone?: (error?: string) => void;
    productTexturePath?: string;
    /** Seamless material texture for surface products — drives the Surface 3D Studio. */
    productSurfaceTexture?: string;
    /** True when the linked product is a flat floor covering (rug/carpet/mat) —
     *  the 3D Studio lays the model flat on the floor so captures read flat. */
    isRug?: boolean;
    /**
     * Source asset URL for recipes that chain off a parent generation —
     * currently used by Variation's change-angle mode to show the image
     * the user is reframing inside the 3D camera-control widget.
     */
    sourceImageUrl?: string;
    /**
     * If true, the embedded 3D camera widget hides itself (used while
     * an in-workspace overlay is open — the widget's portal sits on
     * document.body at zIndex 900 and would otherwise float over the
     * modal).
     */
    widgetHidden?: boolean;
    /** Faded / non-interactive rendering in workspace select mode. */
    dimmed?: boolean;
    /** Folded to just its name and a Generate button. Agent-added nodes start
     *  this way so a plan's results are what fills the screen. */
    collapsed?: boolean;
    onToggleCollapse?: () => void;
}

const NODE_WIDTH = 320;

/**
 * The Recipe node is the creative heart of the canvas. It shows
 * illustrated selectable cards for each recipe input (room type, style,
 * framing, backdrop, lighting, aspect ratio) instead of boring dropdowns.
 *
 * A Play ▶ button at the bottom fires the generation.
 */
export const RecipeNode: React.FC<RecipeNodeProps> = ({
    nodeId,
    x,
    y,
    recipe,
    lifestyleSceneRecipe,
    inputs,
    onInputChange,
    selected,
    zoom,
    onDrag,
    onClick,
    onGenerate,
    onDuplicate,
    onRemove,
    onTargetInChat,
    generating,
    creditBalance,
    onUploadStyleRef,
    onDetectReferenceObjects,
    productApplicationTypes,
    productVisualizationType,
    productModelPath,
    placeRoomUrl,
    placeProducts,
    roomApi,
    boardProducts,
    onRemoveBackground,
    onAddBoardProducts,
    productUuid,
    headlessCaptureAngle,
    headlessCaptureCameraView,
    onAskAgent,
    onMatchReferenceCamera,
    onHeadlessCaptureDone,
    productTexturePath,
    productSurfaceTexture,
    isRug,
    sourceImageUrl,
    widgetHidden,
    dimmed,
    collapsed,
    onToggleCollapse,
}) => {
    const { t } = useTranslation();
    /**
     * A stable array, even when there is nothing in it.
     *
     * `boardProducts || []` built a NEW empty array on every render, which the
     * composer took as a changed dependency — the outer half of the "Maximum
     * update depth exceeded" loop.
     */
    const boardProductsSafe = React.useMemo(() => boardProducts || EMPTY_PRODUCTS, [boardProducts]);


    const seedI18n = useSeedI18n();

    // Accordion state — which section is expanded (null = all collapsed)
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    // From Reference — detect replaceable objects in the uploaded style image
    // (server-side via onDetectReferenceObjects → our API → segment-service, so
    // there's no browser→segment CORS issue). Results feed the chips in
    // StyleReferenceSection via inputs._detectedObjects / _detectingObjects.
    //
    // Refs (not deps/state) so re-renders — including the parent recreating the
    // onDetectReferenceObjects callback, or our own onInputChange writes — can't
    // re-run this effect and cancel the in-flight request. We only apply a
    // response if the reference URL is still the one we requested for.
    const [matchingCamera, setMatchingCamera] = React.useState(false);
    /** The vendor asked to frame the shot themselves rather than measure it. */
    const [frameByHand, setFrameByHand] = React.useState(false);
    const [matchCameraError, setMatchCameraError] = React.useState<string | null>(null);
    const detectReqUrlRef = React.useRef<string | null>(null);
    const onDetectRef = React.useRef(onDetectReferenceObjects);
    onDetectRef.current = onDetectReferenceObjects;
    const onInputChangeRef = React.useRef(onInputChange);
    onInputChangeRef.current = onInputChange;
    const styleRefUrl: string | undefined = inputs.styleReferenceUrl;
    React.useEffect(() => {
        const set = onInputChangeRef.current;
        // Surface From Reference maps the material onto the reference's floor/walls
        // (visualizer pipeline) — nothing gets replaced, so skip object detection.
        if (recipe.slug !== 'from-reference' || productVisualizationType === 'surface'
            || !onDetectRef.current || !styleRefUrl) {
            detectReqUrlRef.current = null;
            return;
        }
        if (detectReqUrlRef.current === styleRefUrl) return; // already handled this image
        detectReqUrlRef.current = styleRefUrl;
        set('_detectingObjects', true);
        set('_detectedObjects', []);
        onDetectRef.current(styleRefUrl)
            .then((objs) => {
                if (detectReqUrlRef.current !== styleRefUrl) return; // superseded by a newer upload
                set('_detectedObjects', objs || []); // full objects (label + bbox) for the hover overlay
            })
            .catch(() => { if (detectReqUrlRef.current === styleRefUrl) set('_detectedObjects', []); })
            .finally(() => { if (detectReqUrlRef.current === styleRefUrl) set('_detectingObjects', false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipe.slug, styleRefUrl, productVisualizationType]);
    // Video confirmation modal — fires before submitting any video
    // generation. Vendors can't reliably predict video output quality
    // and the spend is 50–250× a typical image, so we gate it behind
    // an explicit confirm to prevent accidental burn.
    const [videoConfirmOpen, setVideoConfirmOpen] = useState(false);

    // Schema rules (visible inputs, derived flags, validation) live in
    // ../recipe-schema so the planner can apply exactly the same ones to
    // whatever it proposes. Don't reimplement them here.
    const schemaContext = useMemo(() => ({
        recipe,
        inputs,
        productVisualizationType: productVisualizationType as CsAppliesTo | undefined,
        productApplicationTypes,
    }), [recipe, inputs, productVisualizationType, productApplicationTypes]);

    // `sceneDetails` is declared as an input so the planner's coercion and the
    // validator agree it exists, but the card renders it through its own
    // SceneDetailsInput in the Prompt tab. Leaving it in this list drew the box
    // a second time in Prompt mode and once more in Options mode where it does
    // nothing — and the accordion's auto-advance would step onto a section that
    // renders nothing at all.
    // `agentOnly` inputs stay in the schema so the planner can set them and the
    // validator still accepts them; they just never get a field on the card.
    const recipeInputs = useMemo(
        () => resolveVisibleInputs(schemaContext).filter((i) => i.key !== 'sceneDetails' && !i.agentOnly),
        [schemaContext],
    );


    // AI auto-pick toggle — supported per-recipe. Stored as a regular
    // input key so it survives canvasGraph persistence. When ON, the
    // recipe-specific rows render disabled and the agent decides them
    // at generation time (+5 credits).
    //   - lifestyle-scene: agent picks environment / lighting / stylingHints
    //   - image-to-video:  agent picks cameraMove / intensity / lightingTransition
    // Recipes that use a `mode` selector at the top of the card instead
    // of the legacy `autoPick` boolean. Each recipe defines its own set
    // of modes and default. For Product Photography: options / ai /
    // preset. For Variation: prompt / change-angle. The "Use AI" header
    // button is suppressed on these recipes since the mode selector
    // replaces it.
    // All derived state comes from the shared schema rules — see ../recipe-schema.
    const schemaFlags = useMemo(() => resolveRecipeFlags(schemaContext), [schemaContext]);
    const {
        surfaceMode, isSurfaceApplied, use3dOn, roomSceneStudio,
        isFromRefSurface, fromRefExact, fromRefSameStructure, fromRefStudio, appliedProductOnly, autoPickOn,
        isSurfaceSingle,
        hasMode: HAS_MODE, modeValue, modeTabs,
    } = schemaFlags;
    /**
     * Single Piece · the preset tab stores the preset it is showing.
     *
     * The picker renders `inputs.preset || the recipe default`, so it looks
     * answered while the node carries nothing — and Generate then blocks on a
     * field that is visibly filled in. Same trap as the aspect-ratio toolbar,
     * fixed the same way: write down what is on screen.
     */
    React.useEffect(() => {
        if (!isSurfaceSingle || modeValue !== 'preset') return;
        if (inputs.preset !== undefined && inputs.preset !== null && inputs.preset !== '') return;
        const def = (recipe.inputs || []).find((i: any) => i.key === 'preset');
        const first = (def?.options || []).find((o: any) => !o.appliesTo
            || o.appliesTo.includes(productVisualizationType));
        const seed = first?.value || def?.default;
        if (seed) onInputChange('preset', seed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSurfaceSingle, modeValue, inputs.preset, productVisualizationType]);

    // Seed the mode-specific defaults so a select never opens on an empty "—".
    React.useEffect(() => {
        if (!isSurfaceApplied) return;
        if (use3dOn) {
            // In-Room · Custom uses roomType as the Environment + style as the aesthetic.
            if (inputs.roomType == null || inputs.roomType === '') onInputChange('roomType', 'living-room');
            if (inputs.style == null || inputs.style === '') onInputChange('style', 'modern-minimal');
        } else {
            // Lifestyle uses the visualizer pipeline's surface + environment fields.
            const surf = inputs.surface || 'floor';
            if (inputs.surface == null || inputs.surface === '') onInputChange('surface', 'floor');
            const wantWall = surf === 'walls' || surf === 'wall';
            const firstEnv = wantWall ? 'wall-kitchen-backsplash' : 'floor-living-room';
            const env = inputs.environment;
            const mismatched = env && env !== 'custom' && !String(env).startsWith(wantWall ? 'wall-' : 'floor-');
            if (env == null || env === '' || mismatched) onInputChange('environment', firstEnv);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSurfaceApplied, use3dOn, inputs.roomType, inputs.surface, inputs.environment]);
    // Lifestyle Scene gets Prompt / Options / Use AI tabs (same shape as Product
    // Photography) — furniture only (surface uses the visualizer pipeline with its
    // own fields, no prompt/3D concept).
    // The rules pick which tabs exist; this only resolves their labels, which
    // is why they come back as i18n keys rather than strings.
    const MODE_TABS = useMemo(
        () => modeTabs?.map((tab) => ({
            value: tab.value,
            label: t(tab.labelKey, tab.labelFallback ?? ''),
            hint: t(tab.hintKey, tab.hintFallback ?? ''),
        })),
        [modeTabs, t],
    );
    // Furniture recipes that expose the 3D camera. For these the 3D toggle and
    // aspect ratio live together in a compact toolbar row (aspect is hidden from
    // the accordion below).
    const has3dCamera = (recipe.slug === 'product-closeup'
        || recipe.slug === 'lifestyle-scene'
        || recipe.slug === 'from-reference') && !!productModelPath;
    // Surface (non-3D) level-1 recipes show the aspect ratio in a top toolbar
    // (like the furniture 3D toolbar) instead of down in the accordion. Extend
    // the slug list as each surface recipe is migrated.
    const aspectInputDef = (recipe.inputs || []).find((i: any) => i.key === 'aspectRatio');
    const showAspectTop = !has3dCamera && !!aspectInputDef
        && productVisualizationType === 'surface'
        && ['product-closeup'].includes(recipe.slug);
    // Image to Video gets the same top-toolbar treatment: format and output
    // resolution are frame-level choices, so they belong above the creative
    // options rather than buried among them.
    const isImageToVideo = recipe.slug === 'image-to-video';
    const resolutionInputDef = (recipe.inputs || []).find((i: any) => i.key === 'resolution');
    // The video toolbar is too narrow for the resolution toggle AND the open
    // ratio picker, so the toggle steps aside while the picker is open.
    const [videoAspectOpen, setVideoAspectOpen] = useState(false);
    // In Preset mode, open the "preset" option by default so the vendor sees
    // the preset choices without an extra click. Only re-fires when the mode
    // changes, so manually collapsing it afterwards is respected.
    useEffect(() => {
        if (modeValue === 'preset' && (recipe.inputs || []).some((i: any) => i.key === 'preset')) {
            setExpandedKey('preset');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modeValue]);
    // Migrate legacy Lifestyle Scene nodes: "Use AI" used to be a sub-toggle in
    // the Options tab (stored as autoPick=true). Surface that as the dedicated
    // Use AI tab so the tab, the flag, the worker and pricing all agree.
    useEffect(() => {
        if (recipe.slug === 'lifestyle-scene' && productVisualizationType === 'model3d' && HAS_MODE) {
            const ap = inputs.autoPick === true || inputs.autoPick === 'true';
            if (ap && modeValue !== 'ai') onInputChange('mode', 'ai');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // The 3D studio is mandatory wherever it exists. The camera is where the
    // depth, the mask and the framing come from; a scene generated from words
    // alone is the drifting-angle path this whole pipeline replaced. Products
    // the studio cannot pose (no model, no material) keep the old path — a
    // requirement nobody can satisfy is just a locked door.
    // ...except where there is nothing to pose. An exact copy of the vendor's
    // own photograph has no camera to choose: the reference IS the shot, and
    // its surface is replaced in place. Forcing the studio on there made a
    // route that needs no camera demand one, and quietly took the branch away.
    // ...and Single Piece, where the studio was the wrong tool from the start.
    // It poses a real slab and has the model paint around it, which is right
    // for a material laid in a room and never was for a photograph OF the
    // product: a tile held up in a café is the product's own picture with a
    // scene around it, and the product's own picture is the better place to
    // start. Every framing, pose and joint defect on this card came from
    // reconciling a rendered 3D frame with a photograph nobody took.
    const studioAvailable = (has3dCamera
        || (productVisualizationType === 'surface'
            && ['product-closeup', 'lifestyle-scene', 'from-reference'].includes(recipe.slug)))
        && !fromRefExact && !fromRefSameStructure && !isSurfaceSingle
        // A detail shot is made from the product's own photograph.
        && !(recipe.slug === 'product-closeup' && inputs.shot === 'detail' && inputs.detailCamera !== 'studio');
    const isDetailShot = recipe.slug === 'product-closeup' && inputs.shot === 'detail' && inputs.detailCamera !== 'studio';
    useEffect(() => {
        const on = inputs.use3dCamera === true || inputs.use3dCamera === 'true';
        if (studioAvailable && !on) {
            onInputChange('use3dCamera', true);
            return;
        }
        // And the reverse, which a plan can leave behind: a route that poses
        // nothing has no use for the flag, and carrying it made the canvas wait
        // for a capture from a studio that never opens.
        if (!studioAvailable && on && (fromRefExact || fromRefSameStructure || isSurfaceSingle || isDetailShot)) {
            onInputChange('use3dCamera', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studioAvailable, inputs.use3dCamera, fromRefExact, fromRefSameStructure, isSurfaceSingle, isDetailShot]);
    // Live cost preview — runs the same `resolveCreditCost` formula the
    // backend uses at reservation time. Updates as the vendor toggles
    // resolution / autoPick / mode / duration so the displayed credit
    // number always matches what they'll be charged.
    // Every Room Scene renders through its own recipe now — the Lifestyle mode
    // that submitted (and was priced) under lifestyle-scene is gone.
    const costRecipe = recipe;
    const effectiveCost = useMemo(
        () => resolveCreditCost(costRecipe, { inputs, visualizationType: productVisualizationType }),
        [costRecipe, inputs, productVisualizationType],
    );
    const canAfford = creditBalance === null || creditBalance === undefined || creditBalance >= effectiveCost;

    // ── Required-field validation ────────────────────────────────────
    // Which required field is still blocking Generate, if any. The rules —
    // including every "this row is hidden so it can't block" exception — live
    // in ../recipe-schema alongside the input resolution they mirror.
    const validationError = useMemo<string | null>(
        () => resolveMissingInputs(schemaContext, schemaFlags, recipeInputs)[0] ?? null,
        [schemaContext, schemaFlags, recipeInputs],
    );
    // Block Generate while From Reference object detection is still running.
    const isDetectingObjects = recipe.slug === 'from-reference'
        && (inputs._detectingObjects === true || inputs._detectingObjects === 'true');
    const generateBlocked = !!validationError || isDetectingObjects;
    const AUTO_PICK_SUPPORTED =
        (!HAS_MODE && (
            recipe.slug === 'lifestyle-scene'
            || recipe.slug === 'lifestyle-flatlay'
            || recipe.slug === 'image-to-video'
        ))
        ;
    const AUTO_PICK_LOCKED_KEYS: Set<string> = (() => {
        switch (recipe.slug) {
            case 'image-to-video':
                return new Set(['cameraMove', 'intensity', 'lightingTransition']);
            case 'lifestyle-flatlay':
                // surface stays user-driven (floor vs walls drives the
                // whole composition). AI picks the prop + lighting.
                return new Set(['theme', 'lighting']);
            case 'product-closeup':
                // AI picks the editorial scene + lighting + focus combo that
                // flatters the product; user keeps creative control of
                // framing / camera / aspect / resolution.
                return new Set(['backdrop', 'bgColor', 'lighting', 'shadow', 'depthOfField']);
            default: {
                // lifestyle-scene — fields differ by viz type:
                //   surface  → environment / lighting / stylingHints
                //   model3d  → roomType / style / lighting / stylingHints
                if (productVisualizationType === 'model3d') {
                    return new Set(['roomType', 'style', 'lighting', 'stylingHints']);
                }
                return new Set(['environment', 'lighting', 'stylingHints']);
            }
        }
    })();

    // Per-field AI override tracking. When Use AI is ON, the user can still
    // override individual locked fields (pick their own value). We store a
    // boolean map under `_aiOverrides` in the inputs so it survives canvas
    // persistence and reaches the worker. The worker uses this to skip
    // AI-picking for overridden fields and merge user values in.
    //
    // Rules:
    //   - autoPick ON + field NOT in overrides → AI picks this field → show "AI will pick"
    //   - autoPick ON + field IN overrides → user picked this → show value + 🪄 reset
    //   - autoPick OFF → all fields behave normally
    //   - At least one locked field must be AI-picked for autoPick to stay on.
    //     When the user overrides the last one, autoPick auto-flips OFF and
    //     the override map is cleared (it's no longer meaningful).
    const aiOverrides: Record<string, boolean> = (inputs._aiOverrides && typeof inputs._aiOverrides === 'object')
        ? inputs._aiOverrides
        : {};

    // If the current `surface` value isn't permitted by the product's
    // applicationTypes (e.g. recipe default is "floor" but product is
    // walls-only), auto-switch to the first allowed option. Also resets
    // the dependent environment value so the user sees a fresh, valid
    // combination without having to discover the mismatch themselves.
    useEffect(() => {
        const surfaceInput = recipeInputs.find((i) => i.key === 'surface');
        if (!surfaceInput?.options) return;
        const apps = productApplicationTypes;
        if (!apps || apps.length === 0) return;
        const allowed = new Set<string>();
        apps.forEach((a) => {
            if (a === 'floor') allowed.add('floor');
            if (a === 'walls' || a === 'wall') allowed.add('walls');
        });
        if (allowed.size === 0) return;
        const current = inputs.surface;
        if (current && allowed.has(current)) return; // already valid
        const fallback = surfaceInput.options.find((o) => allowed.has(o.value));
        if (fallback && fallback.value !== current) {
            onInputChange('surface', fallback.value);
            // Reset env so the user picks from the newly filtered list
            const envInput = recipeInputs.find((i) => i.key === 'environment');
            const firstEnv = envInput?.options?.find((o) => optionMatchesSurface(o.value, fallback.value));
            if (firstEnv) onInputChange('environment', firstEnv.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productApplicationTypes, inputs.surface]);

    // Get display label for a selected value
    const getSelectedLabel = (input: CsRecipeInput, val: any): string => {
        if (!val) return '—';
        // A "custom" pick shows the vendor's typed free-text (from the sibling
        // *Custom field) in the collapsed preview, not the placeholder label.
        if (val === 'custom') {
            const customKey = input.key === 'roomType' ? 'roomTypeCustom'
                : input.key === 'environment' ? 'environmentCustom'
                : input.key === 'style' ? 'styleCustom'
                : null;
            const typed = customKey ? (inputs[customKey] || '').toString().trim() : '';
            if (typed) return typed;
        }
        const opt = input.options?.find((o) => o.value === val);
        const fallback = opt?.label || String(val);
        return seedI18n.optionLabel(input.key, val, fallback);
    };

    // Option value prefix matches the surface ("floor" → "floor-*", "walls" → "wall-*").
    // The `custom` option is surface-agnostic — always allow through so the
    // vendor can write their own scene for either floors or walls.
    const optionMatchesSurface = (optionValue: string, surface: any): boolean => {
        if (optionValue === 'custom') return true;
        if (surface === 'floor') return optionValue.startsWith('floor-');
        if (surface === 'walls') return optionValue.startsWith('wall-');
        return true; // no surface picked yet → don't filter
    };

    // Auto-advance: when user picks a value, collapse current and open next.
    // Only fires for card/select inputs — free-form text/textarea inputs stay
    // open so the user can keep typing without the accordion closing mid-word.
    const handleInputChangeWithAdvance = (key: string, value: any, inputIndex: number) => {
        // If user picked a value in an AI-locked row, mark it overridden. If
        // this completes overrides on ALL locked keys, auto-flip autoPick off
        // since there's nothing left for AI to pick (user must keep at least
        // one AI-picked field to justify the +5 credit cost).
        if (autoPickOn && AUTO_PICK_LOCKED_KEYS.has(key) && !aiOverrides[key]) {
            const nextOverrides = { ...aiOverrides, [key]: true };
            const allOverridden = Array.from(AUTO_PICK_LOCKED_KEYS).every((k) => nextOverrides[k]);
            if (allOverridden) {
                // Lifestyle uses a Use AI TAB — leave it for Options so the tab
                // and the autoPick flag stay in sync.
                if (recipe.slug === 'lifestyle-scene') onInputChange('mode', 'options');
                onInputChange('autoPick', false);
                onInputChange('_aiOverrides', {});
            } else {
                onInputChange('_aiOverrides', nextOverrides);
            }
        }

        onInputChange(key, value);

        // Dependent-input reset: when `surface` changes, the currently
        // selected `environment` may no longer be valid for the new surface.
        // Auto-reset so the user picks a fresh one from the filtered list.
        if (key === 'surface') {
            const envInput = recipeInputs.find((i) => i.key === 'environment');
            const currentEnv = inputs.environment;
            const stillValid =
                envInput?.options?.some((o) => o.value === currentEnv && optionMatchesSurface(o.value, value));
            if (!stillValid) {
                const firstMatch = envInput?.options?.find((o) => optionMatchesSurface(o.value, value));
                onInputChange('environment', firstMatch?.value ?? '');
            }
        }

        const input = recipeInputs[inputIndex];
        const hasOptions = !!(input?.options && input.options.length > 0);
        if (!hasOptions) return;
        // Picking the `custom` value opens a free-text affordance. When it's a
        // SEPARATE section below (…Custom), open that section so the vendor
        // immediately sees where to type — otherwise the click looks like a
        // no-op (the row just sits there collapsed). `roomType` is the
        // exception: its custom input is INLINE on the card, so stay put.
        if (value === 'custom') {
            const customKey = `${key}Custom`;
            const hasCustomSection =
                key !== 'roomType' && recipeInputs.some((i) => i.key === customKey);
            if (hasCustomSection) {
                setTimeout(() => setExpandedKey(customKey), 200);
            }
            return;
        }
        // Duration uses a stepper (−/+ buttons), so any single click is
        // a partial action — the vendor is mid-adjustment and likely
        // wants to press +/− several more times. Auto-advancing on the
        // first press would slam the section shut between clicks. Stay
        // open until they collapse manually or move on themselves.
        if (input.key === 'duration') return;
        setTimeout(() => {
            const nextInput = recipeInputs[inputIndex + 1];
            setExpandedKey(nextInput ? nextInput.key : null);
        }, 200);
    };

    // Reset a specific field back to "AI will pick" — clears the override
    // flag. The underlying input value stays (harmless since the UI shows
    // "AI will pick" while the flag is false, and the worker ignores the
    // value for non-overridden locked fields).
    const resetFieldToAi = (key: string) => {
        if (!aiOverrides[key]) return;
        const next = { ...aiOverrides };
        delete next[key];
        onInputChange('_aiOverrides', next);
    };

    // Toggle "Use AI" — turning ON clears any stale override flags so all
    // locked fields start in "AI will pick" state. Turning OFF also clears
    // overrides (they're only meaningful while autoPick is on).
    const toggleAutoPick = () => {
        onInputChange('_aiOverrides', {});
        onInputChange('autoPick', !autoPickOn);
    };

    // The "Use AI" pill (auto-pick toggle), extracted so it can render either in
    // the top header (most recipes) or under the 3D-studio/ratio toolbar (Room
    // Scene · Lifestyle).
    /**
     * What the vendor would have typed, so the chat opens on a real request
     * rather than a blank box. Names the product and the shot, because the
     * agent's first question would otherwise be "which one?".
     */
    const askAgentRequest = () => {
        // The card does not carry the product's name, only its uuid; the agent
        // resolves "the product on this node" from the canvas listing it
        // already receives, so naming the node is enough.
        const what = productUuid ? 'the product on this node' : 'this product';
        const shot = recipe.slug === 'product-closeup' && productVisualizationType === 'surface'
            ? (surfaceMode === 'single' ? 'a product shot' : 'a room scene')
            : (recipe.name || 'a shot');
        return `Set up ${shot} for ${what} — pick the setting and the light for me, in my brand's style.`;
    };

    const useAiPill = (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAskAgent?.(askAgentRequest()); }}
            title={t('CONTENT_STUDIO.RECIPE_ASK_AI_TOOLTIP', 'Ask the agent to set this up — you see every choice it makes') as string}
            aria-label={t('CONTENT_STUDIO.RECIPE_ASK_AI', 'Ask AI') as string}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-transparent px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted hover:text-foreground"
        >
            <Sparkles className="size-3" />
            {t('CONTENT_STUDIO.RECIPE_ASK_AI', 'Ask AI')}
        </button>
    );

    /**
     * Dynamic per-input option filtering:
     *   - `surface`: limit to what the product's applicationTypes allow
     *     (e.g. a floor-only tile shouldn't show Wall).
     *   - `environment`: filter by the currently selected surface
     *     (floor-* vs wall-* prefix).
     */
    const getRenderedInput = (input: CsRecipeInput): CsRecipeInput => {
        if (input.key === 'surface' && input.options) {
            const apps = productApplicationTypes;
            if (!apps || apps.length === 0) return input;
            // Product APPLICATION_TYPE enum uses "floor" / "walls" — same
            // values as our Surface input. Accept singular "wall" too
            // defensively in case older data slipped in.
            const allowed = new Set<string>();
            apps.forEach((a) => {
                if (a === 'floor') allowed.add('floor');
                if (a === 'walls' || a === 'wall') allowed.add('walls');
            });
            if (allowed.size === 0) return input; // unknown types → don't filter
            return {
                ...input,
                options: input.options.filter((o) => allowed.has(o.value)),
            };
        }
        if (input.key === 'environment' && input.options) {
            const surface = inputs.surface;
            if (!surface) return input;
            return {
                ...input,
                options: input.options.filter((o) => optionMatchesSurface(o.value, surface)),
            };
        }
        return input;
    };

    /**
     * The compact card.
     *
     * A plan that adds four recipes fills the screen with settings nobody is
     * reading — the vendor asked for pictures, and the pictures are what they
     * want to look at. Nodes the agent added start folded; anything added by
     * hand opens as it always has, because that is a node being configured.
     */
    // Not while the studio is posing itself. The 3D widget lives in the body
    // below, and a folded card does not render it — so a plan that asked for a
    // named angle applied, collapsed, and then sat there: no capture, and so no
    // generation either. It opens for the few seconds the capture takes.
    if (collapsed && !headlessCaptureAngle && !headlessCaptureCameraView) {
        return (
            <NodeShell
                nodeId={nodeId}
                x={x}
                y={y}
                width={NODE_WIDTH}
                zoom={zoom}
                selected={selected}
                accent="#111827"
                dimmed={dimmed}
                label={surfaceShotName(recipe.slug, inputs, productVisualizationType)
                    || seedI18n.recipeName(recipe.slug, recipe.name)}
                onDrag={onDrag}
                onClick={onClick}
                pulse={generating}
                bottomAffordance={
                    <NodeActions onDuplicate={onDuplicate} onTargetInChat={onTargetInChat} onRemove={onRemove} />
                }
            >
                <div className="flex flex-col gap-2.5 px-4 py-3.5">
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggleCollapse?.(); }}
                        className="flex w-full items-center justify-between gap-2 border-none bg-transparent p-0 text-start text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                            {t('CONTENT_STUDIO.SHOW_SETTINGS', 'Show settings')}
                        </span>
                        <ChevronDown className="size-3.5 shrink-0" />
                    </button>

                    <GenerateButton
                        generating={generating}
                        canAfford={canAfford}
                        blocked={generateBlocked}
                        cost={effectiveCost}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!generating && canAfford && !generateBlocked) onGenerate?.();
                        }}
                    />
                </div>
            </NodeShell>
        );
    }

    return (
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent="#111827"
            dimmed={dimmed}
            // The recipe's own name is the node's top label (like the product
            // node), so the name + credits no longer live inside the card. Surface
            // Product Photography is split into two recipe cards → label by shot type.
            label={surfaceShotName(recipe.slug, inputs, productVisualizationType)
                || seedI18n.recipeName(recipe.slug, recipe.name)}
            onDrag={onDrag}
            onClick={onClick}
            pulse={generating}
            // Duplicate / Remove show as circular buttons on node hover (bottom),
            // not behind a "+" panel — the recipe node has no "add next step".
            bottomAffordance={
                <NodeActions onDuplicate={onDuplicate} onTargetInChat={onTargetInChat} onRemove={onRemove} />
            }
        >
            {/* Header — only rendered when the recipe supports the "Use AI"
                auto-pick toggle. The recipe name lives in the node's top label and
                Remove lives in the bottom hover buttons, so there's no title/close
                row otherwise (avoids an empty header). Image to Video leaves it
                out: the hover "target in chat" button already asks the agent. */}
            {AUTO_PICK_SUPPORTED && !isImageToVideo && (
            <div className="flex items-center justify-end px-4 py-2.5">
                {useAiPill}
            </div>
            )}

            {/* Mode selector — segmented 3-way toggle replaces the stand-
                ard accordion row for the `mode` input on recipes that use
                a mode system (currently product-closeup only). */}
            {/* 3D camera + aspect toolbar — placed ABOVE the mode tabs for
                furniture recipes (Variation etc. are unaffected: no has3dCamera). */}
            {has3dCamera && (() => {
                const on = inputs.use3dCamera === true || inputs.use3dCamera === 'true';
                const aspectInput = (recipe.inputs || []).find((i: any) => i.key === 'aspectRatio');
                const aspectOptions: Array<{ value: string; label?: string }> = withCanonicalAspects(aspectInput?.options);
                const currentAspect = inputs.aspectRatio || aspectInput?.default;
                return (
                    <>
                        <div className="flex items-center justify-end gap-2.5 px-4 pt-4 pb-2">
                            <AspectRatioControl
                                options={aspectOptions}
                                value={currentAspect}
                                onChange={(v) => onInputChange('aspectRatio', v)}
                            />
                            <StudioToggleButton
                                on={on}
                                onClick={() => { if (!on) onInputChange('use3dCamera', true); }}
                                title="3D camera — required: the shot is framed here, not described"
                            />
                        </div>
                        {/**
                          * How the scene gets composed, for Lifestyle Scene.
                          *
                          * Both routes end in the same place — one image handed
                          * to the room renderer — so this is a switch inside the
                          * recipe rather than a second recipe. The 3D studio
                          * settles the camera and poses one piece; a moodboard
                          * settles what is in the room and lets the renderer
                          * find the camera. Studio stays the default, so every
                          * existing node behaves exactly as it does today.
                          */}
                        {recipe.slug === 'lifestyle-scene' && (
                            <div className="flex items-center gap-2 px-4 pt-2 pb-3">
                                <span className="text-xs font-medium text-muted-foreground">
                                    {t('CONTENT_STUDIO.COMPOSE', 'Compose')}
                                </span>
                                {[
                                    { v: 'studio', label: t('CONTENT_STUDIO.COMPOSE_STUDIO', '3D Studio') },
                                    { v: 'moodboard', label: t('CONTENT_STUDIO.COMPOSE_MOODBOARD', 'Moodboard') },
                                ].map((c) => {
                                    const active = (inputs.compose || 'studio') === c.v;
                                    return (
                                        <PillChoice
                                            key={c.v}
                                            active={active}
                                            onClick={() => {
                                                const from = (inputs.compose || 'studio') as string;
                                                if (from === c.v) return;
                                                /**
                                                 * Each route keeps its OWN frame.
                                                 *
                                                 * Clearing the capture on every switch did stop
                                                 * one route inheriting the other's picture — but
                                                 * it also threw the vendor's work away: arrange a
                                                 * board, glance at the 3D studio, come back and
                                                 * the preview was gone. Stashing under the route
                                                 * that produced it keeps both promises, and the
                                                 * board's own arrangement was never lost anyway.
                                                 *
                                                 * Safe as separate writes: the node updater is
                                                 * functional, so each one sees the previous.
                                                 */
                                                const stash = from === 'moodboard' ? 'moodboardSnapshotUrl' : 'studioSnapshotUrl';
                                                const restore = c.v === 'moodboard' ? 'moodboardSnapshotUrl' : 'studioSnapshotUrl';
                                                onInputChange(stash, inputs.snapshotUrl || '');
                                                if (from === 'studio') {
                                                    // Studio-only facts, which a board capture blanks.
                                                    onInputChange('studioCameraView', inputs.cameraView || '');
                                                    onInputChange('studioFurnitureRoom', inputs.furnitureRoom === true);
                                                }
                                                onInputChange('compose', c.v);
                                                onInputChange('snapshotUrl', inputs[restore] || '');
                                                onInputChange('cameraView', c.v === 'studio' ? (inputs.studioCameraView || '') : '');
                                                onInputChange('furnitureRoom', c.v === 'studio' ? inputs.studioFurnitureRoom === true : false);
                                            }}
                                        >
                                            {c.label}
                                        </PillChoice>
                                    );
                                })}
                            </div>
                        )}
                        {inputs.compose === 'moodboard' && recipe.slug === 'lifestyle-scene' ? (
                            <div className="border-t px-4 py-2">
                                <MoodboardComposer
                                    products={boardProductsSafe}
                                    aspectRatio={inputs.aspectRatio}
                                    // The board's shape IS the output's shape, so the
                                    // choice belongs where the arranging happens too.
                                    aspectOptions={aspectOptions}
                                    onAspectRatioChange={(v) => onInputChange('aspectRatio', v)}
                                    onAddProducts={onAddBoardProducts}
                                    snapshotUrl={inputs.snapshotUrl}
                                    board={inputs.moodboard as any}
                                    onBoardChange={(b) => onInputChange('moodboard', b)}
                                    onCapture={async (file) => {
                                        if (!onUploadStyleRef) { throw new Error('upload not available'); }
                                        return onUploadStyleRef(file);
                                    }}
                                    // Imported pictures go through the same store as a
                                    // captured board, so the arrangement keeps working
                                    // after a reload.
                                    onUploadImage={onUploadStyleRef}
                                    onRemoveBackground={onRemoveBackground}
                                    onSnapshotUpdated={(url) => {
                                        onInputChange('snapshotUrl', url);
                                        // A board has no camera to report, and the room
                                        // branch must not read one left by the studio.
                                        onInputChange('cameraView', '');
                                        onInputChange('furnitureRoom', false);
                                    }}
                                />
                            </div>
                        ) : on && (
                            <div className="border-t px-4 py-2">
                                <Furniture3DStudioWidget
                                    // A plan can ask for an angle and have the
                                    // studio capture it without anyone opening
                                    // the dialog. Cleared by the page once done,
                                    // so it fires once per request.
                                    headlessAngle={headlessCaptureAngle}
                                    onHeadlessDone={onHeadlessCaptureDone}
                                    modelPath={productModelPath}
                                    productUuid={productUuid}
                                    snapshotUrl={inputs.snapshotUrl}
                                    sceneState={inputs.studioScene as any}
                                    // The posed scene, stored alongside the frame it produced — so a
                                    // reload reopens the studio where it was left instead of asking
                                    // for the shot to be built again from nothing.
                                    onSceneStateChange={(scene) => onInputChange('studioScene', scene)}
                                    aspectRatio={inputs.aspectRatio}
                                    onAspectRatioChange={(v) => onInputChange('aspectRatio', v)}
                                    referenceImageUrl={recipe.slug === 'from-reference' ? inputs.styleReferenceUrl : undefined}
                                    isRug={isRug}
                                    /**
                                     * Room mode is offered to Product Photography too.
                                     *
                                     * The studio's Room panel — walls, floor size,
                                     * wall height — was gated to Lifestyle Scene, but
                                     * nothing downstream needs it to be: the worker
                                     * forces `scene = 'room'` on a `furnitureRoom`
                                     * capture "whichever recipe invoked the studio",
                                     * and `buildRoomPrompt` reskins the placeholder
                                     * room while keeping the piece and its placement.
                                     *
                                     * Additive on purpose. Room mode defaults OFF, so
                                     * every existing Product Photography node keeps
                                     * the Product-only capture it has today; this only
                                     * makes the panel reachable.
                                     */
                                    allowRoomMode={recipe.slug === 'lifestyle-scene'
                                        || recipe.slug === 'product-closeup'}
                                    onCapture={async (file) => {
                                        if (!onUploadStyleRef) { throw new Error('upload not available'); }
                                        return onUploadStyleRef(file);
                                    }}
                                    onSnapshotUpdated={(url, cameraView, camera, blockouts, furnitureRoom, sceneProps, capturedAngle) => {
                                        onInputChange('snapshotUrl', url);
                                        if (cameraView) onInputChange('cameraView', cameraView);
                                        if (camera) {
                                            onInputChange('lensMm', camera.lensMm);
                                            onInputChange('aperture', camera.aperture);
                                            onInputChange('aspectRatio', camera.aspectRatio);
                                        }
                                        onInputChange('blockouts', blockouts || []);
                                        onInputChange('furnitureRoom', furnitureRoom === true);
                                        // Library furniture the vendor placed. The capture
                                        // shows them; this list is what lets the prompt name
                                        // each one as content to keep.
                                        onInputChange('sceneProps', sceneProps || []);
                                        // What this frame IS, as opposed to what was asked
                                        // for. Cleared on a hand-framed capture so nothing
                                        // downstream claims an angle the picture disproves.
                                        onInputChange('capturedAngle', capturedAngle || null);
                                    }}
                                />
                            </div>
                        )}
                    </>
                );
            })()}

            {/* Surface toolbar (Product Photography · surface) — aspect control +
                a 3D Studio toggle, mirroring the furniture 3D toolbar. The toggle
                is always shown; enabling it reveals the Surface 3D Studio. */}
            {showAspectTop && aspectInputDef && (() => {
                // Room Scene: the 3D toggle switches In-Room (ON — design the shot in
                // the studio) vs Lifestyle (OFF — auto-generate + visualizer pipeline).
                // Single Piece: the toggle is the optional 3D studio for the tile.
                const on = inputs.use3dCamera === true || inputs.use3dCamera === 'true';
                return (
                    <>
                        {/* Aspect ratio + 3D studio toggle — shown first, above the shot type.
                            Extra bottom margin separates it from the content below (surface only). */}
                        <div className="flex items-center justify-end gap-2.5 px-4 py-3">
                            <AspectRatioControl
                                options={withCanonicalAspects(aspectInputDef.options)}
                                value={inputs.aspectRatio || aspectInputDef.default}
                                onChange={(v) => onInputChange('aspectRatio', v)}
                            />
                            {/* No 3D studio on Single Piece: that card photographs the
                                product's own picture and describes the shot in words.
                                The toggle stays for Room Scene, where the material
                                really is posed in a built space. */}
                            {!isSurfaceSingle && (
                            <StudioToggleButton
                                on={on}
                                onClick={() => { if (!on) onInputChange('use3dCamera', true); }}
                                title="3D Studio — required: the shot is framed here, not described"
                            />
                            )}
                        </div>
                        {/* Shot type (Single Piece / In-Room) is now chosen at recipe
                            creation via two separate cards — inputs.surfaceMode is locked,
                            so no in-node toggle here. */}
                        {/* How much scene to build around the installed material.
                            Both options install it and keep the captured angle;
                            the old labels ("With environment" / "Product only")
                            never said so, which is why the card read as a
                            contradiction. */}
                        {/* How much room to build around the installed material.
                            Three answers, not two: the middle one — the shell
                            with nothing standing in it — was reachable only by
                            writing "empty" into a Prompt brief, and it is the
                            shot a surface vendor most often wants. Furniture
                            borrowed from a catalogue competes with the floor
                            they are trying to sell. */}
                        {roomSceneStudio && (
                            <div className="px-4 pb-2">
                                <SegmentedChoice<string>
                                    value={(inputs.appliedEnvironment === false || inputs.appliedEnvironment === 'false')
                                        ? 'none'
                                        : ((inputs.emptyRoom === true || inputs.emptyRoom === 'true') ? 'empty' : 'furnished')}
                                    onChange={(v) => {
                                        onInputChange('appliedEnvironment', v !== 'none');
                                        onInputChange('emptyRoom', v === 'empty');
                                    }}
                                    options={[
                                        {
                                            value: 'furnished',
                                            label: t('CONTENT_STUDIO.SHOT_FULL_ROOM', 'Build a room') as string,
                                            hint: t('CONTENT_STUDIO.SHOT_FULL_ROOM_HINT',
                                                'Build an environment around the installed material.') as string,
                                        },
                                        {
                                            value: 'empty',
                                            label: t('CONTENT_STUDIO.SHOT_EMPTY_ROOM', 'Empty room') as string,
                                            hint: t('CONTENT_STUDIO.SHOT_EMPTY_ROOM_HINT',
                                                'Walls, floor and daylight from your frame — no furniture.') as string,
                                        },
                                        {
                                            value: 'none',
                                            label: t('CONTENT_STUDIO.SHOT_SURFACE_ONLY', 'Surface only') as string,
                                            hint: t('CONTENT_STUDIO.SHOT_SURFACE_ONLY_HINT',
                                                'The material as installed, at your angle — no room invented around it.') as string,
                                        },
                                    ]}
                                />
                            </div>
                        )}
                        {on && onUploadStyleRef && (
                            <div className="border-t px-4 py-2">
                                <Surface3DStudioWidget
                                    poseId={inputs.pose}
                                    headlessAngle={headlessCaptureAngle}
                                    headlessCameraView={headlessCaptureCameraView}
                                    onHeadlessDone={onHeadlessCaptureDone}
                                    textureUrl={productSurfaceTexture || ''}
                                    productUuid={productUuid}
                                    applicationTypes={productApplicationTypes}
                                    surfaceTarget={inputs.surface}
                                    aspectRatio={inputs.aspectRatio}
                                    onAspectRatioChange={(v) => onInputChange('aspectRatio', v)}
                                    mode={surfaceMode}
                                    lockMode
                                    snapshotUrl={inputs.snapshotUrl}
                                    initialCamera={inputs.studioCamera || undefined}
                                    onCapture={async (file) => onUploadStyleRef(file)}
                                    onSnapshotUpdated={(url, meta) => {
                                        onInputChange('snapshotUrl', url);
                                        if (meta) {
                                            // The framing itself, on the node — so
                                            // "Re-frame" reopens where the shot was
                                            // left even after a reload, and after an
                                            // agent capture that mounted the studio
                                            // only long enough to pose it.
                                            if (meta.camera) onInputChange('studioCamera', meta.camera);
                                            onInputChange('aspectRatio', meta.aspectRatio);
                                            // Which surfaces carry the material — the worker keeps those
                                            // exact and reskins the rest (so wall clads stay on the walls).
                                            onInputChange('appliedMatFloor', meta.matFloor !== false);
                                            onInputChange('appliedMatWalls', meta.matWalls === true);
                                            // Any clad surface mapped on just an area → the worker keeps
                                            // the textured region exact and reskins the surrounding grey.
                                            onInputChange('appliedInset', meta.appliedInset === true);
                                            // How many pieces are ACTUALLY in the frame.
                                            // The pose says how many to build; only the
                                            // capture knows how many were built — and
                                            // telling the model a number the picture does
                                            // not contain is asking it to invent the
                                            // difference.
                                            if (typeof meta.pieces === 'number') onInputChange('pieces', meta.pieces);
                                            // Blockout furniture masses → the worker places real pieces there.
                                            // Real pieces now, not grey masses: the capture SHOWS them, and this
                                onInputChange('sceneProps', meta.sceneProps || []);
                                // list is what lets the prompt name each one as content to keep.
                                // Blockouts are cleared so a node that had them does not keep
                                // asking the model to invent furniture that is now really there.
                                onInputChange('blockouts', []);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </>
                );
            })()}

            {/* Place in a room — choose a room on the canvas, pose the product in it. */}
            {recipe.slug === 'place-in-room' && (
                <PlaceInRoomSection
                    inputs={inputs}
                    onInputChange={onInputChange}
                    roomUrl={placeRoomUrl}
                    products={placeProducts || []}
                    roomApi={roomApi}
                    onUpload={onUploadStyleRef}
                />
            )}

            {/* From Reference · surface — WHAT the vendor wants back.
                Not a dial on one pipeline: the answers are different pipelines,
                and the card follows. Keep-the-photo poses nothing, so the studio
                and the scene fields go away with it. */}
            {isFromRefSurface && onUploadStyleRef && (
                <div className="px-4 py-3">
                    <ChoiceSlider<string>
                        caption={t('CONTENT_STUDIO.FROM_REF_AXIS', 'How much of your photo to keep') as string}
                        value={fromRefExact ? 'exact' : (fromRefSameStructure ? 'inspired' : 'loose')}
                        onChange={(v) => {
                            onInputChange('matchStrength', v);
                            // Only the posed route has a camera to choose. On
                            // the other two a capture left set would keep
                            // asking to be taken, for a shot nobody frames.
                            onInputChange('use3dCamera', v === 'loose');
                        }}
                        options={[
                            {
                                value: 'exact',
                                label: t('CONTENT_STUDIO.FROM_REF_KEEP_PHOTO', 'Keep the photo') as string,
                                hint: t('CONTENT_STUDIO.FROM_REF_KEEP_PHOTO_HINT',
                                    'Your picture comes back unchanged, with only the floor or wall replaced.') as string,
                            },
                            {
                                value: 'inspired',
                                label: t('CONTENT_STUDIO.FROM_REF_KEEP_ROOM', 'Keep the room') as string,
                                hint: t('CONTENT_STUDIO.FROM_REF_KEEP_ROOM_HINT',
                                    'The same walls, windows and camera — refurnished around your product.') as string,
                            },
                            {
                                value: 'loose',
                                label: t('CONTENT_STUDIO.FROM_REF_KEEP_LOOK', 'Keep the look') as string,
                                hint: t('CONTENT_STUDIO.FROM_REF_KEEP_LOOK_HINT',
                                    'A new shot of your product, posed at this photo’s camera and matched to its style.') as string,
                            },
                        ]}
                    />
                    {fromRefStudio && aspectInputDef && (
                        <div className="mt-2.5 flex justify-end">
                            <AspectRatioControl
                                options={aspectInputDef.options || []}
                                value={inputs.aspectRatio || aspectInputDef.default}
                                onChange={(v) => onInputChange('aspectRatio', v)}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* The reference picture itself — one control, on every route.
                It belongs to the recipe rather than to any position of the
                slider: all three routes read the same photograph, and moving
                the slider must not disturb the picture it is about. */}
            {recipe.slug === 'from-reference' && onUploadStyleRef && (
                <StyleReferenceSection
                    url={inputs.styleReferenceUrl}
                    uploading={false}
                    detecting={productVisualizationType !== 'surface' && !!inputs._detectingObjects}
                    detectedObjects={productVisualizationType === 'surface' ? [] : (Array.isArray(inputs._detectedObjects) ? inputs._detectedObjects : [])}
                    selectedObject={(inputs.targetObject || '').toString()}
                    onUpload={async (file) => {
                        // Upload to S3, then set the URL — the detection effect
                        // above picks up the new styleReferenceUrl and runs YOLO
                        // detection server-side (no browser→segment CORS issue).
                        const url = await onUploadStyleRef(file);
                        onInputChange('styleReferenceUrl', url);
                    }}
                    onRemove={() => {
                        onInputChange('styleReferenceUrl', undefined);
                        onInputChange('_detectedObjects', []);
                        onInputChange('_detectingObjects', false);
                    }}
                    onSelectObject={(label) => onInputChange('targetObject', label)}
                />
            )}

            {/* From Reference · Studio — pose the surface + camera in 3D; the capture
                is reskinned with the reference's style (objects + lighting) by the AI. */}
            {fromRefStudio && onUploadStyleRef && (
                <div className="border-t px-4 py-2">
                    {/* Stand where the photographer stood — one press.
                        Between the picture and the studio because that is the order of
                        the thought: this is the reference, take its camera, here is the
                        shot. The angle is the hardest thing to match by hand and the
                        thing a reference states most precisely; leaving it to the eye
                        is the drift this pipeline exists to remove. */}
                    {onMatchReferenceCamera && inputs.styleReferenceUrl && (
                        <div className="pb-2.5">
                            <button
                                type="button"
                                disabled={matchingCamera}
                                onClick={async () => {
                                    setMatchingCamera(true);
                                    setMatchCameraError(null);
                                    try {
                                        const err = await onMatchReferenceCamera(inputs.styleReferenceUrl);
                                        if (err) setMatchCameraError(err);
                                    } catch (e: any) {
                                        setMatchCameraError(e?.message || 'Could not read the camera');
                                    } finally {
                                        setMatchingCamera(false);
                                    }
                                }}
                                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-background py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-60"
                            >
                                {matchingCamera && <Loader2 className="size-3 animate-spin" />}
                                {matchingCamera
                                    ? t('CONTENT_STUDIO.FROM_REF_MATCHING_VIEW', 'Reading the camera…')
                                    : t('CONTENT_STUDIO.FROM_REF_MATCH_VIEW', 'Capture the same view')}
                            </button>
                            {/* Framing by hand is the fallback, not the front
                                door — but it has to exist, because a reference
                                whose camera cannot be read would otherwise
                                leave nowhere to go. */}
                            {!inputs.snapshotUrl && !frameByHand && (
                                <button
                                    type="button"
                                    onClick={() => setFrameByHand(true)}
                                    className="mx-auto mt-1.5 block border-none bg-transparent p-0 text-[11px] text-muted-foreground underline transition-colors hover:text-foreground"
                                >
                                    {t('CONTENT_STUDIO.FROM_REF_FRAME_BY_HAND', 'or frame it myself')}
                                </button>
                            )}
                            {matchCameraError && (
                                <p className="mt-1 text-[11px] text-destructive">
                                    {matchCameraError}
                                </p>
                            )}
                        </div>
                    )}
                    {/* The studio opens once there is something to look at.
                        Showing an empty 3D room next to a button that fills it
                        asks the vendor to choose between two ways of doing the
                        same job, when one of them is measured from their own
                        photograph and the other is guesswork by hand. */}
                    {(inputs.snapshotUrl || frameByHand
                        // A plan that names an angle expects the studio to pose
                        // itself and hand back a frame. Hidden, it never mounts,
                        // and the node waits forever for a capture nobody is
                        // able to take — the same deadlock, through a new door.
                        || headlessCaptureAngle || headlessCaptureCameraView) && (
                    <Surface3DStudioWidget
                        headlessAngle={headlessCaptureAngle}
                        headlessCameraView={headlessCaptureCameraView}
                        onHeadlessDone={onHeadlessCaptureDone}
                        textureUrl={productSurfaceTexture || ''}
                        productUuid={productUuid}
                        applicationTypes={productApplicationTypes}
                        surfaceTarget={inputs.surface}
                        aspectRatio={inputs.aspectRatio}
                        onAspectRatioChange={(v) => onInputChange('aspectRatio', v)}
                        mode="applied"
                        lockMode
                        snapshotUrl={inputs.snapshotUrl}
                        initialCamera={inputs.studioCamera || undefined}
                        onCapture={async (file) => onUploadStyleRef(file)}
                        onSnapshotUpdated={(url, meta) => {
                            onInputChange('snapshotUrl', url);
                            if (meta) {
                                if (meta.camera) onInputChange('studioCamera', meta.camera);
                                onInputChange('aspectRatio', meta.aspectRatio);
                                onInputChange('appliedMatFloor', meta.matFloor !== false);
                                onInputChange('appliedMatWalls', meta.matWalls === true);
                                onInputChange('appliedInset', meta.appliedInset === true);
                                // Real pieces now, not grey masses: the capture SHOWS them, and this
                                onInputChange('sceneProps', meta.sceneProps || []);
                                // list is what lets the prompt name each one as content to keep.
                                // Blockouts are cleared so a node that had them does not keep
                                // asking the model to invent furniture that is now really there.
                                onInputChange('blockouts', []);
                            }
                        }}
                    />
                    )}
                </div>
            )}

            {/* Applied · Product only — no scene to configure; just a note. */}
            {appliedProductOnly && (
                <div className="px-4 pt-2 pb-3">
                    <NoteBox>
                        The surface is shown on its own with realistic lighting — no room or environment is built.
                    </NoteBox>
                </div>
            )}

            {/* Variation aspect toolbar — aspect ratio on top (like other recipes)
                for prompt / close-up. change-angle keeps the source aspect (Qwen
                ignores an output size), so it's hidden there. In close-up this
                ratio also locks the crop box shape. */}
            {recipe.slug === 'style-variation' && (modeValue === 'prompt' || modeValue === 'close-up') && (() => {
                const aspectInput = (recipe.inputs || []).find((i: any) => i.key === 'aspectRatio');
                if (!aspectInput) return null;
                return (
                    <div className="flex items-center justify-end px-4 pt-3 pb-2">
                        <AspectRatioControl
                            options={withCanonicalAspects(aspectInput.options)}
                            value={inputs.aspectRatio || aspectInput.default}
                            onChange={(v) => onInputChange('aspectRatio', v)}
                        />
                    </div>
                );
            })()}

            {/* Image to Video toolbar — format + output resolution sit above
                the creative options. Note we pass the SEEDED aspect options
                rather than withCanonicalAspects(): the worker crops the source
                to the ratio before animating, and only the seeded ratios are
                ones that crop keeps the subject in frame for. */}
            {isImageToVideo && (aspectInputDef || resolutionInputDef) && (
                <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
                    {resolutionInputDef ? (
                        <div
                            aria-hidden={videoAspectOpen}
                            className={cn(
                                'transition-opacity duration-200',
                                videoAspectOpen && 'pointer-events-none invisible w-0 overflow-hidden opacity-0',
                            )}
                        >
                            <SegmentedToggle
                                options={resolutionInputDef.options || []}
                                value={inputs.resolution ?? resolutionInputDef.default ?? '720p'}
                                onChange={(v) => onInputChange('resolution', v)}
                            />
                        </div>
                    ) : <div />}
                    {aspectInputDef && (
                        <AspectRatioControl
                            options={aspectInputDef.options}
                            value={inputs.aspectRatio || aspectInputDef.default}
                            onChange={(v) => onInputChange('aspectRatio', v)}
                            onOpenChange={setVideoAspectOpen}
                        />
                    )}
                </div>
            )}

            {HAS_MODE && MODE_TABS && !appliedProductOnly && (
                <ModeSelector
                    mode={modeValue}
                    tabs={MODE_TABS}
                    dense={showAspectTop}
                    onChange={(next) => {
                        // Switching modes clears any stale AI overrides.
                        onInputChange('_aiOverrides', {});
                        // Lifestyle Scene's worker + pricing key off the legacy
                        // `autoPick` flag, so keep it in sync with the Use AI tab.
                        // Other mode recipes are mode-based → just clear the flag.
                        if (recipe.slug === 'lifestyle-scene') {
                            onInputChange('autoPick', next === 'ai');
                        } else if (inputs.autoPick) {
                            onInputChange('autoPick', false);
                        }
                        onInputChange('mode', next);
                    }}
                />
            )}

            {/**
              * Why the 3D camera is not here.
              *
              * Change angle and Close-up both reshoot an EXISTING picture, so
              * they need one: `sourceImageUrl` is the linked generation's asset.
              * On a Variation node that hangs off anything else there is nothing
              * to reshoot — and the tab was still selectable, so the control
              * simply failed to appear and the card looked broken. Saying so is
              * the whole fix; the widget itself was never gone.
              */}
            {recipe.slug === 'style-variation'
                && (modeValue === 'change-angle' || modeValue === 'close-up')
                && !sourceImageUrl && (
                <div className="px-4 pb-3">
                    <div className="rounded-md border border-dashed border-border bg-muted p-3 text-[11px] leading-relaxed text-muted-foreground">
                        {t(
                            'CONTENT_STUDIO.VARIATION_NEEDS_SOURCE',
                            'This mode reshoots a picture you have already generated. Connect this card to a generated image to use it.',
                        )}
                    </div>
                </div>
            )}

            {/* Change-angle widget — renders above the accordion rows
                only for Variation in change-angle mode. The 3 number
                inputs (azimuth/elevation/distance) are hidden below
                since this widget drives them. */}
            {recipe.slug === 'style-variation' && modeValue === 'change-angle' && sourceImageUrl && (
                <div className="px-4 pb-2">
                    <CameraControl3D
                        sourceImageUrl={sourceImageUrl}
                        hidden={widgetHidden}
                        azimuth={Number(inputs.azimuth) || 0}
                        elevation={Number(inputs.elevation) || 0}
                        distance={Number(inputs.distance) || 5}
                        onChange={(next) => {
                            onInputChange('azimuth', next.azimuth);
                            onInputChange('elevation', next.elevation);
                            onInputChange('distance', next.distance);
                        }}
                    />
                </div>
            )}

            {/* Close-up crop widget — Variation · close-up mode. The vendor draws
                the exact region to zoom into + picks lens / depth of field; the
                worker crops that rect and Flux-enhances it into a macro. */}
            {recipe.slug === 'style-variation' && modeValue === 'close-up' && sourceImageUrl && (
                <CloseUpCropWidget
                    sourceImageUrl={sourceImageUrl}
                    crop={inputs.closeUpCrop}
                    lens={Number(inputs.closeUpLens) || 100}
                    aperture={Number(inputs.closeUpAperture) || 2.8}
                    aspectRatio={inputs.aspectRatio || '1:1'}
                    onCropChange={(c) => onInputChange('closeUpCrop', c)}
                    onLensChange={(mm) => onInputChange('closeUpLens', mm)}
                    onApertureChange={(f) => onInputChange('closeUpAperture', f)}
                />
            )}

            {/* 3D rotation widget — Variation · 3d-rotation mode only.
                The vendor rotates the product's actual GLB, and each
                saved pose uploads a snapshot that becomes IMAGE 1 for
                the worker's Flux Klein pose-rotation pipeline. */}
            {recipe.slug === 'style-variation'
                && modeValue === '3d-rotation'
                && productModelPath && (
                <div className="px-4 pb-2">
                    <ProductPoseWidget
                        modelPath={productModelPath}
                        productUuid={productUuid}
                        poseSnapshotUrl={inputs.poseSnapshotUrl}
                        onCapture={async (file) => {
                            if (!onUploadStyleRef) {
                                throw new Error('upload not available');
                            }
                            return onUploadStyleRef(file);
                        }}
                        onSnapshotUpdated={(url) => onInputChange('poseSnapshotUrl', url)}
                    />
                </div>
            )}

            {/* 3D camera — furniture only. OFF → normal flow. ON → pose the real
                camera in the 3D Studio; the capture drives the result (Product Photography
                → preset/backdrop scene; Lifestyle Scene → a full furnished room; From
                Reference → the posed frame is swapped into the reference scene — match the
                reference's angle in the studio). */}
            {/* Lifestyle Scene · Use AI tab — hint that AI picks the scene from the
                capture; the environment/style/lighting rows below render as
                "AI will pick" (overridable). */}
            {recipe.slug === 'lifestyle-scene' && productModelPath && modeValue === 'ai' && (
                <div className="px-4 pb-3">
                    <NoteBox icon={<Sparkles className="size-3" />}>
                        AI reads your captured framing + product and picks the environment, style &amp; lighting. Click any row below to set it yourself instead.
                    </NoteBox>
                </div>
            )}

            {/* Lifestyle Scene · Prompt tab. The vendor describes anything —
                environment, lighting, props to add/remove — with total freedom; the
                worker AI-refines it. {{reference}} means the product. */}
            {recipe.slug === 'lifestyle-scene' && productModelPath && modeValue === 'prompt' && (
                <div className="px-4 pb-3">
                    <SceneDetailsInput
                        value={inputs.sceneDetails || ''}
                        onChange={(v) => onInputChange('sceneDetails', v)}
                        badgeLabel="Product"
                        placeholder="Describe the scene: environment, lighting, props to add or remove…"
                    />
                </div>
            )}

            {/* Product Photography · furniture · Prompt tab.
                The tab is offered on every product type, but the box behind it
                was written three times — for Lifestyle, for Single Piece and for
                Surface Applied — and furniture was in none of them. Picking
                Prompt on a chair gave a tab with nothing on it at all.

                A backdrop rather than a room: this is a product shot, and the
                worker maps the brief onto `backdropOnly`. The camera is not the
                brief's to set — it comes from the capture, as in Preset. */}
            {recipe.slug === 'product-closeup' && productVisualizationType === 'model3d'
                && modeValue === 'prompt' && (
                <div className="px-4 pb-3">
                    <SceneDetailsInput
                        value={inputs.sceneDetails || ''}
                        onChange={(v) => onInputChange('sceneDetails', v)}
                        badgeLabel="Product"
                        placeholder="Describe the shot: the surface it sits on, the light, the mood…"
                    />
                </div>
            )}

            {/* Image to Video · Prompt tab — what happens in the clip, in the
                vendor's words (a hand unrolls the rug, the camera drops low).
                The Product badge resolves to the product the image shows. */}
            {isImageToVideo && modeValue === 'prompt' && (
                <div className="px-4 pb-3">
                    <SceneDetailsInput
                        value={inputs.sceneDetails || ''}
                        onChange={(v) => onInputChange('sceneDetails', v)}
                        badgeLabel="Product"
                        placeholder={t('CONTENT_STUDIO.VIDEO_PROMPT_PLACEHOLDER',
                            'Describe what happens: a hand slowly unrolls the product, the camera glides to a low angle…') as string}
                    />
                </div>
            )}

            {/* Surface · Single Piece — the SCENE, always on the page.
                Not a tab any more. The pose above says how the piece is
                presented and this says where it is, and those are two
                questions rather than two alternatives: every pose needs a
                setting, and the nine curated "presets" this replaces were nine
                settings for one unchanging pose. */}
            {isSurfaceSingle && modeValue === 'prompt' && (
                <div className="px-4 pb-3">
                    <SceneDetailsInput
                        value={inputs.sceneDetails || ''}
                        onChange={(v) => onInputChange('sceneDetails', v)}
                        badgeLabel="Surface"
                        placeholder={t('CONTENT_STUDIO.SINGLE_SCENE_PLACEHOLDER',
                            'Describe the setting: the surface it sits on, the light, the mood…') as string}
                    />
                </div>
            )}

            {/* Surface · Applied · Prompt — describe the room/scene built around the
                captured surface (e.g. add furniture on the floor, choose the mood).
                The captured 3D frame keeps the material's placement + perspective. */}
            {recipe.slug === 'product-closeup' && productVisualizationType === 'surface'
                && surfaceMode === 'applied' && modeValue === 'prompt' && (
                <div className="px-4 pb-3">
                    <SceneDetailsInput
                        value={inputs.sceneDetails || ''}
                        onChange={(v) => onInputChange('sceneDetails', v)}
                        badgeLabel="Surface"
                        placeholder="Describe the scene around the surface: lighting, mood, furniture or props to place on it…"
                    />
                </div>
            )}

            {/* 3D Studio widget — studio-3d recipe. The vendor frames the
                furniture's GLB with a free camera (orbit + zoom) on a neutral
                backdrop + soft shadow; the captured view becomes inputs.snapshotUrl
                and the worker renders a realistic room around that exact angle. */}
            {recipe.slug === 'studio-3d' && productModelPath && (
                <div className="px-4 pb-2">
                    <Furniture3DStudioWidget
                        modelPath={productModelPath}
                        productUuid={productUuid}
                        snapshotUrl={inputs.snapshotUrl}
                        sceneState={inputs.studioScene as any}
                        // The posed scene, stored alongside the frame it produced — so a
                        // reload reopens the studio where it was left instead of asking
                        // for the shot to be built again from nothing.
                        onSceneStateChange={(scene) => onInputChange('studioScene', scene)}
                        aspectRatio={inputs.aspectRatio}
                        isRug={isRug}
                        onCapture={async (file) => {
                            if (!onUploadStyleRef) {
                                throw new Error('upload not available');
                            }
                            return onUploadStyleRef(file);
                        }}
                        onSnapshotUpdated={(url, cameraView, camera, blockouts, _furnitureRoom, sceneProps, capturedAngle) => {
                            onInputChange('snapshotUrl', url);
                            if (cameraView) onInputChange('cameraView', cameraView);
                            if (camera) {
                                onInputChange('lensMm', camera.lensMm);
                                onInputChange('aperture', camera.aperture);
                                onInputChange('aspectRatio', camera.aspectRatio);
                            }
                            onInputChange('blockouts', blockouts || []);
                            onInputChange('sceneProps', sceneProps || []);
                            onInputChange('capturedAngle', capturedAngle || null);
                        }}
                    />
                </div>
            )}

            {/* Scene: Studio | Room (studio-3d). Room reveals the room/style/lighting
                inputs below and tells the worker to build a lifestyle room. */}
            {recipe.slug === 'studio-3d' && (
                <div className="flex items-center gap-2 px-4 pb-3">
                    <span className="text-xs font-medium text-muted-foreground">Scene</span>
                    {[{ v: 'studio', label: 'Studio' }, { v: 'room', label: 'Room' }].map((s) => (
                        <PillChoice
                            key={s.v}
                            active={(inputs.scene || 'studio') === s.v}
                            onClick={() => onInputChange('scene', s.v)}
                        >
                            {s.label}
                        </PillChoice>
                    ))}
                    {(inputs.scene || 'studio') === 'room' && (
                        <PillChoice
                            className="ms-1 gap-1"
                            active={inputs.autoPick === true || inputs.autoPick === 'true'}
                            onClick={() => onInputChange('autoPick', !(inputs.autoPick === true || inputs.autoPick === 'true'))}
                            title="Let AI design the scene from your captured framing + product"
                        >
                            <Sparkles className="me-1 inline size-3 align-[-2px]" />
                            Use AI
                        </PillChoice>
                    )}
                </div>
            )}

            {/* AI mode hint — AI designs room/style/lighting/composition from the capture. */}
            {recipe.slug === 'studio-3d' && (inputs.scene || 'studio') === 'room'
                && (inputs.autoPick === true || inputs.autoPick === 'true') && (
                <div className="px-4 pb-3">
                    <NoteBox icon={<Sparkles className="size-3" />}>
                        AI will read your captured framing + product and design the room, style, lighting and composition (where the window, decor and negative space go). Turn off <b className="font-medium text-foreground">Use AI</b> to set it yourself.
                    </NoteBox>
                </div>
            )}

            {/* Room scene details — free text for specifics like "window on the right". Manual mode only. */}
            {recipe.slug === 'studio-3d' && (inputs.scene || 'studio') === 'room'
                && !(inputs.autoPick === true || inputs.autoPick === 'true') && (
                <div className="px-4 pb-3">
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                        Scene details <span className="font-normal text-muted-foreground/70">(optional)</span>
                    </p>
                    <textarea
                        rows={2}
                        value={inputs.sceneDetails || ''}
                        onChange={(e) => onInputChange('sceneDetails', e.target.value)}
                        placeholder="e.g. large window with sheer curtains on the right, potted plant in the corner, warm evening light"
                        className="w-full resize-y rounded-lg border border-input bg-transparent px-2.5 py-2 text-xs leading-snug text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/50"
                    />
                </div>
            )}

            {/* Accordion input sections */}
            <div>
                {recipeInputs.map((rawInput, idx) => {
                    // Some inputs filter their options dynamically (e.g.
                    // `environment` is filtered by the selected `surface`).
                    const input = getRenderedInput(rawInput);

                    // Resolution is retired from the UI for IMAGE recipes —
                    // output is always 1K (2K gave weird results; upscaling
                    // lives on the Edit Image node). The worker treats anything
                    // != '2k' as 1K, so never surfacing it keeps 1K.
                    // Video is a different axis entirely (720p / 1080p frame
                    // size), and it renders in the top toolbar instead.
                    if (input.key === 'resolution') return null;
                    // Aspect ratio lives in the compact top toolbar row for
                    // furniture 3D, surface close-up and video recipes — hide
                    // the accordion version there.
                    if ((has3dCamera || showAspectTop || isImageToVideo) && input.key === 'aspectRatio') return null;

                    // Surface only: the material is installed at the chosen
                    // angle and no room is built, so Environment and Style —
                    // which describe a room's setting and decor — are
                    // questions about nothing. The validator hides them for
                    // the same reason; this is the card keeping step with it.
                    if (appliedProductOnly
                        && ['roomType', 'roomTypeCustom', 'style', 'styleCustom'].includes(input.key)) {
                        return null;
                    }
                    // heroRotation only applies when framing === 'angled-hero'
                    // — hide otherwise. Both in options mode and in preset
                    // mode (the framing override supports angled-hero too).
                    if (input.key === 'heroRotation' && inputs.framing !== 'angled-hero') {
                        return null;
                    }
                    // environmentCustom is the free-text scene description
                    // that pairs with the "Custom" environment option —
                    // hide it unless that option is selected.
                    if (input.key === 'environmentCustom' && inputs.environment !== 'custom') {
                        return null;
                    }
                    // roomTypeCustom is bound INLINE inside the roomType
                    // custom card (see IllustratedOptionCard's inlineInput
                    // props), so it never needs to render as its own row.
                    if (input.key === 'roomTypeCustom') {
                        return null;
                    }
                    // styleCustom mirrors environmentCustom: free-form
                    // description that only appears when style === 'custom'.
                    if (input.key === 'styleCustom' && inputs.style !== 'custom') {
                        return null;
                    }
                    // From Reference · Studio — the 3D studio's "Apply to" drives
                    // floor/walls, so hide the accordion surface field there
                    // (Normal mode keeps it for the visualizer pipeline).
                    if (fromRefStudio && input.key === 'surface') {
                        return null;
                    }
                    // Background color only matters for the "Solid color"
                    // backdrop — hide it otherwise so it isn't a confusing
                    // always-visible field that appears to do nothing.
                    if (input.key === 'bgColor' && inputs.backdrop !== 'solid-color') {
                        return null;
                    }
                    // Sunlit-empty-room material/lighting custom text fields —
                    // each only appears when its parent select is 'custom'.
                    if (input.key === 'wallMaterialCustom' && inputs.wallMaterial !== 'custom') {
                        return null;
                    }
                    if (input.key === 'floorMaterialCustom' && inputs.floorMaterial !== 'custom') {
                        return null;
                    }
                    if (input.key === 'lightingStyleCustom' && inputs.lightingStyle !== 'custom') {
                        return null;
                    }
                    // studio-3d owns the snapshot + framing (aspect/lens/aperture) via
                    // the 3D Studio widget, so those are always hidden. style/roomType/
                    // lighting only apply to the Room scene — hide them in Studio mode.
                    if (recipe.slug === 'studio-3d') {
                        if (['snapshotUrl', 'aspectRatio'].includes(input.key)) return null;
                        if (['style', 'roomType', 'lighting'].includes(input.key)) {
                            // Only in Room mode, and only when the vendor is choosing
                            // manually — "Use AI" picks them from the capture.
                            const aiOn = inputs.autoPick === true || inputs.autoPick === 'true';
                            if ((inputs.scene || 'studio') !== 'room' || aiOn) return null;
                        }
                    }

                    // Product Photography: prompt-based camera/position controls are
                    // unreliable — the 3D Studio replaces them for both furniture
                    // (model3d) and surface products. Remove them entirely.
                    if (recipe.slug === 'product-closeup'
                        && (productVisualizationType === 'model3d' || productVisualizationType === 'surface')) {
                        if (['framing', 'heroRotation', 'cameraHeight'].includes(input.key)) return null;
                    }

                    // Lifestyle Scene furniture: the Options tab shows environment /
                    // style / lighting; the Prompt tab hides them (the prompt drives
                    // the scene); the Use AI tab renders them as "AI will pick" (via
                    // isAiLockedKey below), so they are NOT hidden there.
                    if (recipe.slug === 'lifestyle-scene' && productVisualizationType === 'model3d' && HAS_MODE) {
                        const optionFields = ['roomType', 'roomTypeCustom', 'style', 'styleCustom', 'lighting'];
                        if (optionFields.includes(input.key) && modeValue === 'prompt') return null;
                    }

                    // Surface · Applied: the material fills a real room, so the
                    // "backdrop" / background-swatch rows never apply, shadows come
                    // from the room lighting, and depth of field is already set by
                    // the studio's f-stop. Hide those everywhere in Applied.
                    // Prompt mode also hides lighting (the description drives it);
                    // Custom keeps only the lighting row.
                    if (recipe.slug === 'product-closeup' && productVisualizationType === 'surface') {
                        // `environment` belonged to the Lifestyle mode, which is gone.
                        // Room Scene asks for `roomType`; neither card shows these.
                        if (['surface', 'environment', 'environmentCustom'].includes(input.key)) {
                            return null;
                        }
                        // Single Piece is one page: the pose row and the scene
                        // brief are rendered above as themselves, and the rest
                        // of the old preset machinery is gone. `lighting` goes
                        // with it — a sentence about light beats a seven-item
                        // list, and it is the same sentence either way.
                        if (isSurfaceSingle
                            && ['mode', 'pose', 'sceneDetails', 'lighting', 'backdrop',
                                'snapshotUrl', 'aspectRatio',
                                'bgColor', 'shadow', 'depthOfField', 'framing', 'heroRotation',
                                'cameraHeight', 'roomType'].includes(input.key)) {
                            return null;
                        }
                        // Product-only: no scene fields at all (just relight the surface).
                        if (appliedProductOnly
                            && ['backdrop', 'bgColor', 'shadow', 'depthOfField', 'lighting', 'roomType'].includes(input.key)) {
                            return null;
                        }
                        // Environment (roomType) + Style (injected) belong to In-Room ·
                        // Custom only.
                        if (['roomType', 'style', 'styleCustom'].includes(input.key) && !(roomSceneStudio && modeValue === 'options')) {
                            return null;
                        }
                        if (roomSceneStudio && HAS_MODE) {
                            // `emptyRoom` is drawn by the Build-a-room choice above,
                            // so its accordion row would ask the same question twice
                            // and let the two answers disagree.
                            const alwaysHidden = new Set(['backdrop', 'bgColor', 'shadow', 'depthOfField', 'emptyRoom']);
                            if (alwaysHidden.has(input.key)) return null;
                            if (modeValue === 'prompt' && input.key === 'lighting') return null;
                        }
                    }

                    // Field visibility rules for the mode system.
                    if (HAS_MODE) {
                        // The mode selector above renders this row; skip
                        // the regular accordion version.
                        if (input.key === 'mode') return null;
                        // Preset picker is only relevant in preset mode.
                        if (input.key === 'preset' && modeValue !== 'preset') return null;
                        // Fields with appliesToPreset are PRESET-SPECIFIC
                        // enhancers (e.g. wall/floor materials for the
                        // Sunlit empty room preset). Hide them entirely
                        // outside preset mode — they'd otherwise leak
                        // into Options / Use AI with no AI coverage.
                        {
                            const ap = (input as any).appliesToPreset as string[] | undefined;
                            if (ap && ap.length > 0) {
                                if (modeValue !== 'preset') return null;
                                const activePreset = inputs.preset;
                                if (!activePreset || !ap.includes(activePreset)) return null;
                            }
                        }
                        // Preset bakes scene + lighting + shadow into its
                        // hand-tuned prompt, so hide those scene-building
                        // rows. Camera controls (framing + cameraHeight)
                        // stay editable so the user can tune the locked
                        // look without abandoning preset mode.
                        if (modeValue === 'preset') {
                            const hiddenInPreset = new Set([
                                'backdrop', 'bgColor', 'lighting', 'shadow',
                                'depthOfField',
                            ]);
                            if (hiddenInPreset.has(input.key)) return null;
                        }
                        // Image to Video · Prompt: the brief describes the
                        // motion, so the preset camera rows would be a second,
                        // contradicting answer.
                        if (isImageToVideo && modeValue === 'prompt'
                            && ['cameraMove', 'intensity', 'lightingTransition'].includes(input.key)) {
                            return null;
                        }
                        // Variation: each mode hides the rows the other
                        // modes own — the active widget / prompt IS the UI
                        // for that mode.
                        if (recipe.slug === 'style-variation') {
                            const cameraKeys = new Set(['azimuth', 'elevation', 'distance']);
                            const promptKeys = new Set(['variationStrength', 'stylePrompt']);
                            const closeUpKeys = new Set(['closeUpZoom']);
                            // aspectRatio now lives in the top toolbar (prompt /
                            // close-up); change-angle keeps the source aspect
                            // (Qwen ignores an output size). Always hide the
                            // accordion row.
                            if (input.key === 'aspectRatio') return null;
                            // closeUpZoom is close-up-only — hide in every
                            // other mode.
                            if (modeValue !== 'close-up' && closeUpKeys.has(input.key)) return null;
                            if (modeValue === 'prompt' && (cameraKeys.has(input.key) || closeUpKeys.has(input.key))) return null;
                            if (modeValue === 'change-angle' && (promptKeys.has(input.key) || closeUpKeys.has(input.key))) return null;
                            if (modeValue === 'change-angle' && cameraKeys.has(input.key)) return null;
                            // 3d-rotation owns pose via the GLB widget —
                            // hide camera knobs AND prompt fields; keep
                            // only aspectRatio / resolution (if any).
                            if (modeValue === '3d-rotation'
                                && (cameraKeys.has(input.key) || promptKeys.has(input.key) || closeUpKeys.has(input.key))) {
                                return null;
                            }
                            // Close-up now uses the crop widget (drawn region + lens
                            // + DOF) — the old closeUpZoom preset and the aspect row
                            // are retired (the crop rect drives the framing/aspect).
                            if (modeValue === 'close-up'
                                && (cameraKeys.has(input.key) || promptKeys.has(input.key)
                                    || closeUpKeys.has(input.key) || input.key === 'aspectRatio')) {
                                return null;
                            }
                        }
                    }

                    // Preset stays permanently open (never collapses) — the vendor
                    // always sees the preset choices. Other options collapse as usual.
                    // Gallery-style inputs whose previews ARE the choice stay
                    // pinned open — collapsing them to a text chip throws away
                    // the whole point (scene presets, camera-move clips).
                    const alwaysOpen = input.key === 'preset'
                        || (input.key === 'cameraMove' && isImageToVideo);
                    const isExpanded = alwaysOpen || expandedKey === input.key;
                    const selectedVal = inputs[input.key];
                    const selectedLabel = getSelectedLabel(input, selectedVal);
                    // When Use AI is on, a field is "AI-picked" unless the
                    // user explicitly overrode it. Locked-but-not-overridden
                    // rows show "AI will pick" but stay clickable so the
                    // user can override. Overridden rows show the user's
                    // value + a small 🪄 reset icon to hand it back to AI.
                    const isAiLockedKey = autoPickOn && AUTO_PICK_LOCKED_KEYS.has(input.key);
                    const isAiPicking = isAiLockedKey && !aiOverrides[input.key];
                    const isOverriddenField = isAiLockedKey && !!aiOverrides[input.key];

                    return (
                        <div key={input.key}>
                            {/* Collapsed row — click to expand (preset is locked open). */}
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (alwaysOpen) return;
                                    setExpandedKey(isExpanded ? null : input.key);
                                }}
                                className={cn(
                                    'flex items-center justify-between border-t px-4 py-2 transition-colors hover:bg-muted',
                                    alwaysOpen ? 'cursor-default' : 'cursor-pointer',
                                    isExpanded && 'bg-muted',
                                )}
                            >
                                <span className="text-xs font-medium text-muted-foreground">
                                    {seedI18n.inputLabel(input.key, input.label)}
                                </span>
                                <div className="flex max-w-[170px] items-center gap-1">
                                    {isOverriddenField && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                resetFieldToAi(input.key);
                                            }}
                                            title={t('CONTENT_STUDIO.RECIPE_AI_RESET_FIELD')}
                                            aria-label={t('CONTENT_STUDIO.RECIPE_AI_RESET_FIELD')}
                                            className="inline-flex size-[18px] items-center justify-center rounded-full border border-border bg-muted text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                                        >
                                            <Wand2 className="size-2.5" />
                                        </button>
                                    )}
                                    <div
                                        className={cn(
                                            'flex max-w-[160px] items-center gap-1.5 overflow-hidden text-[11px] font-medium text-ellipsis whitespace-nowrap',
                                            selectedVal ? 'text-foreground' : 'text-muted-foreground/70',
                                        )}
                                    >
                                        {/* Colour swatch next to the value for colour
                                            inputs (type=color) and the bgColor swatch
                                            select — collapsed row shows the picked colour,
                                            not just text. bgColor's effective colour is the
                                            custom backdropColor hex if set, else the chosen
                                            preset's hex (parsed from its label). The hex is
                                            data, so it stays an inline style. */}
                                        {(input.type === 'color' || input.key === 'bgColor') && !isAiPicking && (() => {
                                            let hex: string;
                                            if (input.type === 'color') {
                                                hex = (typeof selectedVal === 'string' && selectedVal) ? selectedVal : (input.default || '#F5F1EA');
                                            } else {
                                                const custom = (inputs.backdropColor || '').toString().trim();
                                                if (/^#?[0-9a-fA-F]{6}$/.test(custom)) {
                                                    hex = custom.startsWith('#') ? custom : `#${custom}`;
                                                } else {
                                                    const opt = (input.options || []).find((o: any) => o.value === selectedVal);
                                                    hex = (opt && (opt.label.match(/#[0-9A-Fa-f]{6}/) || [])[0]) || '#CCCCCC';
                                                }
                                            }
                                            return (
                                                <span
                                                    className="size-[13px] shrink-0 rounded-full border border-border"
                                                    style={{ backgroundColor: hex }}
                                                />
                                            );
                                        })()}
                                        {/* Backdrop preview swatch — the collapsed row shows
                                            the selected backdrop's texture/colour (solid-color
                                            resolves to the chosen background colour, else its
                                            rainbow). */}
                                        {input.key === 'backdrop' && !isAiPicking && (() => {
                                            const isSolid = selectedVal === 'solid-color';
                                            const swatch = BACKDROP_SWATCHES[selectedVal];
                                            let style: React.CSSProperties = {};
                                            if (isSolid) {
                                                const custom = (inputs.backdropColor || '').toString().trim();
                                                let hex = '';
                                                if (/^#?[0-9a-fA-F]{6}$/.test(custom)) {
                                                    hex = custom.startsWith('#') ? custom : `#${custom}`;
                                                } else {
                                                    const bgOpts = (recipe.inputs || []).find((i: any) => i.key === 'bgColor')?.options || [];
                                                    const bgOpt = bgOpts.find((o: any) => o.value === inputs.bgColor);
                                                    hex = (bgOpt && (bgOpt.label.match(/#[0-9A-Fa-f]{6}/) || [])[0]) || '';
                                                }
                                                style = hex ? { backgroundColor: hex } : { background: SOLID_COLOR_SWATCH };
                                            } else if (swatch?.image) {
                                                style = { backgroundImage: `url(${swatch.image})`, backgroundSize: 'cover', backgroundPosition: 'center' };
                                            } else {
                                                style = { background: swatch?.gradient || '#FFFFFF' };
                                            }
                                            return (
                                                <span
                                                    className="size-[13px] shrink-0 rounded-full border border-border"
                                                    style={style}
                                                />
                                            );
                                        })()}
                                        {isAiPicking ? (
                                            // AI-pick chip — an inked pill, so "the agent
                                            // owns this row" reads at a glance without
                                            // adding colour noise to the card.
                                            <span
                                                title={t('CONTENT_STUDIO.RECIPE_AI_WILL_PICK')}
                                                className="inline-flex items-center gap-1 bg-foreground px-1.5 py-0.5 text-[9.5px] font-medium tracking-wider text-background"
                                            >
                                                <Sparkles className="size-2.5" />
                                                AI
                                            </span>
                                        ) : (
                                            selectedLabel
                                        )}
                                        <ChevronDown
                                            className={cn(
                                                'size-3 shrink-0 text-muted-foreground/70 transition-transform',
                                                isExpanded && 'rotate-180',
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Expanded content */}
                            <AnimatePresence>
                                {isExpanded && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <RecipeInputSection
                                            input={input}
                                            value={selectedVal}
                                            onChange={(v) => handleInputChangeWithAdvance(input.key, v, idx)}
                                            productVisualizationType={productVisualizationType}
                                            allInputs={inputs}
                                            onAnyInputChange={onInputChange}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>

            {/* Generate button */}
            <div className="border-t bg-muted px-4 py-3">
                <BorderBeam
                    size="line"
                    colorVariant="ocean"
                    duration={2.4}
                    strength={0.66}>
                    <GenerateButton
                        generating={generating}
                        canAfford={canAfford}
                        blocked={generateBlocked}
                        cost={effectiveCost}
                        showIcon
                        onClick={(e) => {
                            e.stopPropagation();
                            if (generating || !canAfford || generateBlocked) return;
                            // Video generations gate behind a confirm
                            // dialog — see comment on videoConfirmOpen.
                            if (recipe.outputKind === 'video') {
                                setVideoConfirmOpen(true);
                                return;
                            }
                            onGenerate();
                        }}
                    />
                </BorderBeam>
                {isDetectingObjects && canAfford && !generating && (
                    <p className="mt-1.5 text-center text-[11px] text-muted-foreground/70 italic">
                        {t('CONTENT_STUDIO.DETECTING_OBJECTS', 'Detecting objects…')}
                    </p>
                )}
                {validationError && !isDetectingObjects && canAfford && !generating && (
                    <p className="mt-1.5 text-center text-[11px] text-muted-foreground/70 italic">
                        {t('CONTENT_STUDIO.COMPLETE_REQUIRED_FIELDS', 'Complete required fields to generate')}
                    </p>
                )}
                {!canAfford && !generating && (
                    <p className="mt-1.5 text-center text-[11px] text-destructive">
                        {t('CONTENT_STUDIO.INSUFFICIENT_CREDITS', 'Not enough credits')}
                    </p>
                )}
            </div>

            {/* Video confirmation dialog — only mounted for video
                recipes. Click handler on the Generate button opens it
                instead of firing onGenerate directly. */}
            {recipe.outputKind === 'video' && (
                <Dialog open={videoConfirmOpen} onOpenChange={setVideoConfirmOpen}>
                    <DialogContent
                        className="sm:max-w-[420px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DialogHeader>
                            <DialogTitle>
                                {t('CONTENT_STUDIO.VIDEO_CONFIRM_TITLE', 'Confirm video generation')}
                            </DialogTitle>
                            <DialogDescription>
                                {t(
                                    'CONTENT_STUDIO.VIDEO_CONFIRM_BODY',
                                    'Video generation is unpredictable — the result may not match what you have in mind. The credits will be deducted whether or not you keep the result.',
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="rounded-xl border bg-muted p-3">
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>{t('CONTENT_STUDIO.VIDEO_CONFIRM_COST_LABEL', 'Cost')}</span>
                                <span className="font-medium text-foreground">
                                    {t(
                                        'CONTENT_STUDIO.VIDEO_CONFIRM_COST_VALUE',
                                        '{{credits}} video credits',
                                        { credits: effectiveCost },
                                    )}
                                </span>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setVideoConfirmOpen(false);
                                }}
                            >
                                {t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                            </Button>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setVideoConfirmOpen(false);
                                    onGenerate();
                                }}
                            >
                                {t('CONTENT_STUDIO.VIDEO_CONFIRM_CTA', 'Generate video')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </NodeShell>
    );
};

RecipeNode.displayName = 'RecipeNode';

export const RECIPE_NODE_WIDTH = NODE_WIDTH;

// ═══════════════════════════════════════════════════════════════════════
// SceneDetailsInput — a contentEditable scene-brief field that renders the
// {{reference}} token as an atomic, non-editable badge. The stored value keeps
// the literal {{reference}} token (worker resolves it to the product); only the
// on-screen rendering shows a badge. Insert button adds the badge at the caret.
// ═══════════════════════════════════════════════════════════════════════

const REFERENCE_TOKEN = '{{reference}}';

function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

interface SceneDetailsInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    badgeLabel?: string;
}

const SceneDetailsInput: React.FC<SceneDetailsInputProps> = ({ value, onChange, placeholder, badgeLabel = 'Product' }) => {
    const editorRef = React.useRef<HTMLDivElement | null>(null);

    // Written into the field's own HTML, so the token badge is styled by class
    // like the rest of the card rather than by a frozen inline style string.
    const badgeClass = 'mx-px inline-flex select-all items-center rounded-md border border-border bg-muted px-1 '
        + 'text-[10.5px] font-medium leading-normal whitespace-nowrap text-foreground';

    const badgeHtml = React.useCallback(
        () => `<span data-token="reference" contenteditable="false" class="${badgeClass}">${escapeHtml(badgeLabel)}</span>`,
        [badgeLabel],
    );

    const toHtml = React.useCallback(
        (v: string): string => {
            if (!v) return '';
            return v.split(REFERENCE_TOKEN).map(escapeHtml).join(badgeHtml()).replace(/\n/g, '<br>');
        },
        [badgeHtml],
    );

    const serialize = (el: HTMLElement): string => {
        let out = '';
        const walk = (node: Node) => {
            node.childNodes.forEach((n) => {
                if (n.nodeType === Node.TEXT_NODE) { out += n.textContent || ''; return; }
                if (n.nodeType !== Node.ELEMENT_NODE) return;
                const e = n as HTMLElement;
                if (e.dataset && e.dataset.token === 'reference') { out += REFERENCE_TOKEN; return; }
                if (e.tagName === 'BR') { out += '\n'; return; }
                if (/^(DIV|P)$/.test(e.tagName) && out && !out.endsWith('\n')) out += '\n';
                walk(e);
            });
        };
        walk(el);
        return out;
    };

    // Sync external value → DOM only when it differs from the serialized DOM,
    // so typing doesn't reset the caret.
    React.useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        if (serialize(el) !== value) el.innerHTML = toHtml(value || '');
    }, [value, toHtml]);

    const emit = () => {
        const el = editorRef.current;
        if (!el) return;
        const v = serialize(el);
        if (v === '') el.innerHTML = ''; // keep the :empty placeholder working
        onChange(v);
    };

    const insertReference = () => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        let range: Range;
        if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
            range = sel.getRangeAt(0);
        } else {
            range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
        }
        range.deleteContents();
        const badge = document.createElement('span');
        badge.dataset.token = 'reference';
        badge.setAttribute('contenteditable', 'false');
        badge.setAttribute('class', badgeClass);
        badge.textContent = badgeLabel;
        const space = document.createTextNode(' ');
        range.insertNode(space);
        range.insertNode(badge);
        // Caret after the trailing space.
        range.setStartAfter(space);
        range.collapse(true);
        if (sel) { sel.removeAllRanges(); sel.addRange(range); }
        emit();
    };

    // ── @-mention: typing "@" (optionally + a prefix of the label) pops a
    //    suggestion to insert the product badge. ──────────────────────────
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [mentionOpen, setMentionOpen] = React.useState(false);
    const [mentionPos, setMentionPos] = React.useState<{ left: number; top: number }>({ left: 7, top: 0 });
    const mentionRangeRef = React.useRef<{ node: Node; start: number; end: number } | null>(null);

    const detectMention = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) { setMentionOpen(false); return; }
        const range = sel.getRangeAt(0);
        if (!range.collapsed || range.startContainer.nodeType !== Node.TEXT_NODE) { setMentionOpen(false); return; }
        const node = range.startContainer;
        const offset = range.startOffset;
        const before = (node.textContent || '').slice(0, offset);
        // "@" at the start or after whitespace, then an optional word that is
        // still a prefix of the label (so "@pro" matches, "@xyz" doesn't).
        const m = before.match(/(?:^|\s)@(\w*)$/);
        if (!m) { setMentionOpen(false); mentionRangeRef.current = null; return; }
        const query = m[1].toLowerCase();
        if (query && !badgeLabel.toLowerCase().startsWith(query)) { setMentionOpen(false); mentionRangeRef.current = null; return; }
        mentionRangeRef.current = { node, start: offset - m[1].length - 1, end: offset };
        // Anchor the suggestion just under the caret's own line (not the whole
        // field). Convert the caret's viewport rect into the container's local,
        // zoom-corrected coordinates (the node lives inside a scaled canvas).
        const container = containerRef.current;
        const cr = range.cloneRange().getBoundingClientRect();
        if (container && cr && (cr.top || cr.left || cr.height)) {
            const box = container.getBoundingClientRect();
            const zoom = box.width / (container.offsetWidth || box.width) || 1;
            const left = (cr.left - box.left) / zoom;
            const top = (cr.bottom - box.top) / zoom;
            const maxLeft = Math.max(4, (container.offsetWidth || 240) - 130);
            setMentionPos({ left: Math.max(4, Math.min(left, maxLeft)), top });
        }
        setMentionOpen(true);
    };

    const applyMention = () => {
        const el = editorRef.current;
        const ctx = mentionRangeRef.current;
        setMentionOpen(false);
        mentionRangeRef.current = null;
        if (!el || !ctx) return;
        el.focus();
        const sel = window.getSelection();
        if (!sel) return;
        const range = document.createRange();
        try {
            range.setStart(ctx.node, ctx.start);
            range.setEnd(ctx.node, ctx.end);
        } catch { return; }
        // Select the "@query" text, then reuse the badge inserter (it replaces
        // the current selection) so the "@" is swapped for the product badge.
        sel.removeAllRanges();
        sel.addRange(range);
        insertReference();
    };

    return (
        <div ref={containerRef} className="relative">
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={() => { emit(); detectMention(); }}
                onKeyUp={detectMention}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (!mentionOpen) return;
                    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); applyMention(); }
                    else if (e.key === 'Escape') { setMentionOpen(false); }
                }}
                // Clicking in then out of an empty field can leave a stray <br>,
                // which breaks the :empty placeholder — clear it on blur so the
                // placeholder comes back.
                onBlur={() => {
                    setMentionOpen(false);
                    const el = editorRef.current;
                    if (el && serialize(el).trim() === '') el.innerHTML = '';
                }}
                // The node is draggable — stop pointer/mouse events from starting a
                // drag so a click lands the caret and typing works.
                onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                data-placeholder={placeholder || ''}
                // Stay compact — a long prompt scrolls inside the field.
                className="max-h-[132px] min-h-14 w-full cursor-text overflow-y-auto rounded-lg border border-input px-[11px] pt-[9px] pb-[34px] text-xs leading-relaxed break-words whitespace-pre-wrap text-foreground transition-colors outline-none [overflow-wrap:anywhere] [scrollbar-width:thin] empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)] focus:border-ring focus:ring-1 focus:ring-ring/50"
            />
            {/* Subtle "insert product" chip pinned in the field's bottom-left —
                type "@" in the field to get the same suggestion inline. */}
            <button
                type="button"
                title={`Insert @${badgeLabel} — or type "@" in the field`}
                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                onClick={insertReference}
                className="absolute bottom-[7px] left-[7px] inline-flex items-center gap-0.5 rounded-md border border-border bg-background px-1.5 py-px text-[10.5px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted hover:text-foreground"
            >
                @{badgeLabel}
            </button>
            {/* @-mention suggestion — appears just under the caret's line while an
                "@" query is active; click (or Enter) inserts the product badge. */}
            {mentionOpen && (
                <div className="absolute z-20" style={{ left: mentionPos.left, top: mentionPos.top + 3 }}>
                    <button
                        type="button"
                        // Keep the caret/selection in the editor so applyMention's
                        // range stays valid (mousedown would otherwise blur it).
                        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                        onClick={applyMention}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-popover px-2 py-1.5 text-xs font-medium text-foreground shadow-md transition-colors hover:bg-muted"
                    >
                        <span className="bg-accent px-1 text-foreground">@{badgeLabel}</span>
                        <span className="text-[11px] font-normal text-muted-foreground">your item</span>
                    </button>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Internal: renders one recipe input section with illustrated cards
// ═══════════════════════════════════════════════════════════════════════

interface RecipeInputSectionProps {
    input: CsRecipeInput;
    value: any;
    onChange: (value: any) => void;
    /** 'surface' | 'model3d' | 'image_only' — routes framing options to
     *  tile-based surface variants when applicable. */
    productVisualizationType?: string;
    /** Full input map + setter — used so the `custom` card for roomType
     *  can drive the sibling `roomTypeCustom` value inline. */
    allInputs?: Record<string, any>;
    onAnyInputChange?: (key: string, value: any) => void;
}

const RecipeInputSection: React.FC<RecipeInputSectionProps> = ({ input, value, onChange, productVisualizationType, allInputs, onAnyInputChange }) => {
    const seedI18n = useSeedI18n();
    const hasIllustration = getIllustrationRenderer(input.key);
    // "Visual" = has an illustration renderer OR preview media (still or
    // looping clip, e.g. Preset / camera moves). Those render as a horizontal
    // scroller; plain text enums keep the grid.
    const hasVisual = !!hasIllustration
        || (input.options || []).some((o: any) => (
            !!getOptionPreviewUrl(input.key, o.value)
            || !!getOptionPreviewVideoUrl(input.key, o.value)
        ));
    // Motion previews need more room than a 96px tile to read at a glance.
    const hasVideoPreview = (input.options || []).some(
        (o: any) => !!getOptionPreviewVideoUrl(input.key, o.value),
    );

    // Free-text "…Custom" escape-hatch fields (wallMaterialCustom, styleCustom,
    // etc.) render as their own section that only mounts once the vendor picks
    // "Custom". Focus the field on mount so the cursor lands there and it's
    // obvious the click did something.
    const customTextRef = React.useRef<HTMLTextAreaElement | null>(null);
    const isCustomTextField = /Custom$/.test(input.key);
    React.useEffect(() => {
        if (isCustomTextField && customTextRef.current) {
            customTextRef.current.focus();
        }
    }, [isCustomTextField]);

    return (
        <div className="px-4 pt-1 pb-3">
            {/* The option's label already shows in the collapsed row above, so
                the section doesn't repeat it. */}
            {input.type === 'color' ? (
                <ColorPickerRow
                    value={typeof value === 'string' && value ? value : (input.default || '#F5F1EA')}
                    onChange={onChange}
                />
            ) : input.key === 'bgColor' && input.options && input.options.length > 0 ? (
                // Solid-color background: named presets as colour swatches +
                // a Custom swatch that opens a colour picker. Presets set the
                // bgColor slug (and clear the custom hex); Custom writes the
                // raw hex to backdropColor, which the worker prefers.
                <SolidColorPicker
                    options={input.options as Array<{ value: string; label: string }>}
                    value={value}
                    customHex={allInputs?.backdropColor}
                    onPickPreset={(slug) => { onChange(slug); onAnyInputChange?.('backdropColor', ''); }}
                    onPickCustom={(hex) => onAnyInputChange?.('backdropColor', hex)}
                />
            ) : input.key === 'backdrop' && input.options && input.options.length > 0 ? (
                // Backdrop = circular material swatches (like the colour picker),
                // since it's essentially "what the surface looks like."
                <BackdropPicker
                    options={input.options as Array<{ value: string; label?: string }>}
                    value={value}
                    onChange={onChange}
                />
            ) : input.key === 'duration' && input.options && input.options.length > 0 ? (
                // Duration uses a stepper instead of option cards — the
                // LTX enum is densely packed (6/8/10…/20) and an N-card
                // grid wastes space in the rail. Stepper communicates
                // "fine-grained" naturally and -/+ buttons are clearer
                // than scrolling through 8 visually-identical cards.
                <DurationStepperRow
                    options={input.options}
                    value={value ?? input.default}
                    onChange={onChange}
                />
            ) : input.options && input.options.length > 0 ? (() => {
                const cards = input.options.map((opt) => {
                    const isSelected = value === opt.value;
                    // When the vendor picks the `custom` card on roomType, the card
                    // becomes an inline text input bound to the sibling
                    // `roomTypeCustom` field.
                    const isRoomTypeCustom = input.key === 'roomType' && opt.value === 'custom';
                    return (
                        <IllustratedOptionCard
                            key={opt.value}
                            inputKey={input.key}
                            optionValue={opt.value}
                            optionLabel={seedI18n.optionLabel(input.key, opt.value, opt.label)}
                            selected={isSelected}
                            onClick={() => onChange(opt.value)}
                            productVisualizationType={productVisualizationType}
                            inlineInputValue={
                                isRoomTypeCustom ? (allInputs?.roomTypeCustom as string | undefined) : undefined
                            }
                            onInlineInputChange={
                                isRoomTypeCustom && onAnyInputChange
                                    ? (v) => onAnyInputChange('roomTypeCustom', v)
                                    : undefined
                            }
                        />
                    );
                });
                // All option sets render as a horizontal scroller (edge buttons +
                // fades), so options never wrap into a grid.
                return (
                    <HorizontalScroller
                        scrollToIndex={(input.options || []).findIndex((o: any) => o.value === value)}
                        itemWidth={hasVideoPreview ? 128 : undefined}
                    >
                        {cards}
                    </HorizontalScroller>
                );
            })() : (
                // Text / textarea fallback for free-form inputs. A bare
                // <textarea> rather than the shadcn one: this needs a ref, and
                // on React 18 a plain function component drops it.
                <textarea
                    ref={customTextRef}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={seedI18n.inputLabel(input.key, input.label)}
                    rows={input.type === 'textarea' ? 3 : 1}
                    className="w-full resize-y rounded-lg border border-input bg-transparent px-2.5 py-2 text-xs text-foreground transition-colors outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/50"
                />
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Solid background colour — preset swatches + a Custom colour picker
// ═══════════════════════════════════════════════════════════════════════

/** Small check badge on the corner of a selected circular swatch. */
const SwatchCheckBadge: React.FC = () => (
    <span className="absolute -top-[3px] -right-[3px] flex size-[15px] items-center justify-center rounded-full border-[1.5px] border-background bg-foreground text-background">
        <Check className="size-2" strokeWidth={4} />
    </span>
);

/**
 * Backdrop picker — the backdrop options as circular material swatches (like
 * the solid-colour picker), since a backdrop is essentially "what the surface
 * looks like." Each swatch shows its gradient/texture, tooltip = full name,
 * selected = a check badge.
 */
// The `solid-color` backdrop opens the separate Background-colour picker, so it
// gets a rainbow "pick a colour" swatch rather than a plain white circle.
const SOLID_COLOR_SWATCH = 'conic-gradient(from 0deg, #ef4444, #f59e0b, #eab308, #22c55e, #06b6d4, #6366f1, #a855f7, #ef4444)';

const BackdropPicker: React.FC<{
    options: Array<{ value: string; label?: string }>;
    value: string;
    onChange: (v: string) => void;
}> = ({ options, value, onChange }) => {
    const seedI18n = useSeedI18n();
    const selectedIndex = options.findIndex((o) => o.value === value);
    return (
        // Horizontal scroller (like the other visual options) — labelled circular
        // swatches in a scrollable row, not a grid.
        <HorizontalScroller itemWidth="auto" scrollToIndex={selectedIndex}>
            {options.map((opt) => {
                const isSolid = opt.value === 'solid-color';
                const swatch = BACKDROP_SWATCHES[opt.value];
                const name = seedI18n.optionLabel('backdrop', opt.value, opt.label || opt.value).split('·')[0].trim();
                const selected = value === opt.value;
                return (
                    // Labelled circular swatch — the caption tells the near-white
                    // surfaces apart (and sets a backdrop visually apart from the
                    // bare-dot Background colour picker); tooltip = full name.
                    <Tooltip key={opt.value}>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={() => onChange(opt.value)}
                                aria-label={name}
                                className="group flex w-[60px] flex-col items-center gap-1.5 border-none bg-transparent p-0"
                            >
                                <span
                                    className="relative size-[46px] rounded-full border border-border transition-colors group-hover:border-muted-foreground"
                                    // The swatch IS the option, so its texture stays a value.
                                    style={isSolid
                                        ? { background: SOLID_COLOR_SWATCH }
                                        : swatch?.image
                                        ? { backgroundImage: `url(${swatch.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                        : { background: swatch?.gradient || '#FFFFFF' }}
                                >
                                    {selected && <SwatchCheckBadge />}
                                </span>
                                <span
                                    className={cn(
                                        'line-clamp-2 w-full overflow-hidden text-center text-[9.5px] leading-tight font-medium',
                                        selected ? 'text-foreground' : 'text-muted-foreground',
                                    )}
                                >
                                    {name}
                                </span>
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{name}</TooltipContent>
                    </Tooltip>
                );
            })}
        </HorizontalScroller>
    );
};

const SolidColorPicker: React.FC<{
    options: Array<{ value: string; label: string }>;
    value: string;
    customHex?: string;
    onPickPreset: (slug: string) => void;
    onPickCustom: (hex: string) => void;
}> = ({ options, value, customHex, onPickPreset, onPickCustom }) => {
    const seedI18n = useSeedI18n();
    const isCustom = /^#?[0-9a-fA-F]{6}$/.test((customHex || '').trim());
    const normalizedCustom = isCustom
        ? ((customHex as string).startsWith('#') ? (customHex as string) : `#${customHex}`)
        : '#F5F1EA';

    const CheckBadge = SwatchCheckBadge;

    return (
        <HorizontalScroller itemWidth="auto">
            {options.map((opt) => {
                const hex = (opt.label.match(/#[0-9A-Fa-f]{6}/) || [])[0] || '#CCCCCC';
                const name = seedI18n.optionLabel('bgColor', opt.value, opt.label).split('·')[0].trim();
                const selected = !isCustom && value === opt.value;
                return (
                    <Tooltip key={opt.value}>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={() => onPickPreset(opt.value)}
                                aria-label={name}
                                className="relative size-8 rounded-full border border-border p-0 transition-colors hover:border-muted-foreground"
                                style={{ backgroundColor: hex }}
                            >
                                {selected && <CheckBadge />}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{name}</TooltipContent>
                    </Tooltip>
                );
            })}
            {/* Custom colour — the swatch opens the native picker. */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className="relative flex size-8 cursor-pointer items-center justify-center overflow-visible rounded-full border border-border transition-colors hover:border-muted-foreground"
                        style={{ backgroundColor: isCustom ? normalizedCustom : 'transparent' }}
                    >
                        {isCustom ? <CheckBadge /> : (
                            <span
                                className="flex size-[30px] items-center justify-center rounded-full"
                                style={{ background: SOLID_COLOR_SWATCH }}
                            >
                                <span className="flex size-[18px] items-center justify-center rounded-full bg-background text-[13px] leading-none font-medium text-muted-foreground">+</span>
                            </span>
                        )}
                        <input
                            type="color"
                            value={normalizedCustom}
                            onChange={(e) => onPickCustom(e.target.value)}
                            className="absolute inset-0 size-full cursor-pointer border-none p-0 opacity-0"
                        />
                    </div>
                </TooltipTrigger>
                <TooltipContent side="top">Custom color</TooltipContent>
            </Tooltip>
        </HorizontalScroller>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Illustrated option card — routes to the right illustration by inputKey
// ═══════════════════════════════════════════════════════════════════════

interface IllustratedOptionCardProps {
    inputKey: string;
    optionValue: string;
    optionLabel: string;
    selected: boolean;
    onClick: () => void;
    /** Drives surface vs furniture illustration variants. */
    productVisualizationType?: string;
    /**
     * Opt-in inline text input. When supplied, the card's label area is
     * replaced by a live <input> the moment the card is selected, so
     * the vendor can type a value directly into the card (used for
     * roomType's `custom` card — "type your own room name").
     */
    inlineInputValue?: string;
    onInlineInputChange?: (value: string) => void;
}

// ═══════════════════════════════════════════════════════════════════════
// Color picker — renders a live swatch + native HTML color input + hex
// text field. Value is always a 6-digit hex string ("#RRGGBB").
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// DurationStepperRow — minus / value / plus stepper for video duration.
//
// Driven by the recipe input's `options` array (the LTX enum
// 6/8/10/12/14/16/18/20s). Steps to the previous / next entry on -/+
// click; clamps at the ends. Big readout in the middle reads as the
// "current pick" — softly larger numerals + a "seconds" caption — and
// a thin progress track underneath shows where in the range the value
// sits. Used only for the `duration` input.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Compact segmented pill for a small frame-level choice (video output
 * resolution). A binary pick doesn't warrant a whole accordion row plus
 * card scroller, and it reads better sitting next to the aspect ratio.
 */
const SegmentedToggle: React.FC<{
    options: Array<{ value: any; label?: string }>;
    value: any;
    onChange: (v: any) => void;
}> = ({ options, value, onChange }) => {
    if (!options.length) return null;
    return (
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5">
            {options.map((opt) => {
                const active = String(value) === String(opt.value);
                return (
                    <button
                        key={String(opt.value)}
                        type="button"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); onChange(opt.value); }}
                        className={cn(
                            'h-6 rounded-md border-none px-2.5 text-[11px] font-medium whitespace-nowrap transition-colors hover:text-foreground',
                            active ? 'bg-background text-foreground' : 'bg-transparent text-muted-foreground',
                        )}
                    >
                        {opt.label || String(opt.value)}
                    </button>
                );
            })}
        </div>
    );
};

interface DurationStepperRowProps {
    options: Array<{ value: any; label: string }>;
    value: any;
    onChange: (next: any) => void;
}

const DurationStepperRow: React.FC<DurationStepperRowProps> = ({ options, value, onChange }) => {
    const { t } = useTranslation();
    // Sort numerically so step direction matches what the user expects
    // (smaller → larger left to right) regardless of seed order.
    const sorted = useMemo(
        () => [...options].sort((a, b) => Number(a.value) - Number(b.value)),
        [options],
    );
    const currentIndex = Math.max(0, sorted.findIndex((o) => String(o.value) === String(value)));
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const current = sorted[safeIndex] || sorted[0];
    const atMin = safeIndex === 0;
    const atMax = safeIndex === sorted.length - 1;
    const seconds = Number(current?.value) || 0;
    // Progress 0..1 across the available range — drives the fill on the
    // thin track underneath the readout.
    const progress = sorted.length > 1 ? safeIndex / (sorted.length - 1) : 0;

    // Each +/- press steps immediately to the adjacent value. The
    // accordion section stays expanded across clicks (the recipe-level
    // auto-advance is suppressed for `duration`) so the vendor can keep
    // adjusting without the section collapsing mid-decision.
    const stepBy = (delta: -1 | 1) => {
        const nextIdx = safeIndex + delta;
        if (nextIdx < 0 || nextIdx >= sorted.length) return;
        onChange(sorted[nextIdx].value);
    };
    const minusDisabled = atMin;
    const plusDisabled = atMax;

    const StepperBtn: React.FC<{ disabled: boolean; onClick: () => void; children: React.ReactNode; ariaLabel: string }> = ({ disabled, onClick, children, ariaLabel }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            className="flex size-[38px] items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors not-disabled:hover:border-foreground/40 not-disabled:hover:bg-muted not-disabled:active:scale-[0.96] disabled:cursor-not-allowed disabled:text-muted-foreground disabled:opacity-45"
        >
            {children}
        </button>
    );

    return (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted px-3.5 py-3">
            <div className="flex items-center justify-between gap-3">
                <StepperBtn
                    disabled={minusDisabled}
                    onClick={() => stepBy(-1)}
                    ariaLabel={t('CONTENT_STUDIO.DURATION_DECREASE', 'Decrease duration')}
                >
                    <Minus className="size-4" />
                </StepperBtn>

                <div className="flex flex-1 flex-col items-center justify-center leading-none">
                    {/* A fixed minimum width so 1- vs 2-digit values don't shift
                        the stepper buttons on every click. */}
                    <span className="min-w-14 text-center text-3xl font-medium tracking-tight text-foreground tabular-nums">
                        {seconds}
                    </span>
                    <span className="mt-1 text-[11px] font-medium tracking-widest text-muted-foreground uppercase">
                        {t('CONTENT_STUDIO.DURATION_SECONDS', 'seconds')}
                    </span>
                </div>

                <StepperBtn
                    disabled={plusDisabled}
                    onClick={() => stepBy(1)}
                    ariaLabel={t('CONTENT_STUDIO.DURATION_INCREASE', 'Increase duration')}
                >
                    <Plus className="size-4" />
                </StepperBtn>
            </div>

            {/* Range track — visual cue of where in the supported range
                the current value sits. Fills proportionally from left. */}
            <div className="relative h-1 overflow-hidden bg-border">
                <div
                    className="absolute inset-y-0 left-0 bg-foreground transition-[width] duration-200 ease-out"
                    style={{ width: `${progress * 100}%` }}
                />
            </div>

            {/* Min / max captions sit under the track for orientation. */}
            <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
                <span>{Number(sorted[0]?.value)}s</span>
                <span>{Number(sorted[sorted.length - 1]?.value)}s</span>
            </div>
        </div>
    );
};

interface ColorPickerRowProps {
    value: string;
    onChange: (hex: string) => void;
}

const ColorPickerRow: React.FC<ColorPickerRowProps> = ({ value, onChange }) => {
    const normalized = normalizeHex(value) || '#F5F1EA';
    const [draft, setDraft] = useState(normalized);
    useEffect(() => {
        setDraft(normalized);
    }, [normalized]);

    const commitDraft = () => {
        const next = normalizeHex(draft);
        if (next && next !== normalized) onChange(next);
        else setDraft(normalized);
    };

    return (
        <div className="flex items-center gap-2">
            <label
                className="relative h-9 w-12 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-border transition-colors hover:border-foreground"
                style={{ backgroundColor: normalized }}
            >
                <input
                    type="color"
                    value={normalized}
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    className="absolute inset-0 cursor-pointer border-none p-0 opacity-0"
                />
            </label>
            <Input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitDraft}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                spellCheck={false}
                className="h-9 flex-1 font-mono tracking-wide uppercase"
            />
        </div>
    );
};

function normalizeHex(raw: string): string | null {
    if (!raw) return null;
    let s = raw.trim().toUpperCase();
    if (!s.startsWith('#')) s = '#' + s;
    // Expand 3-digit shorthand (#ABC → #AABBCC).
    if (/^#[0-9A-F]{3}$/.test(s)) {
        s = '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
    }
    if (!/^#[0-9A-F]{6}$/.test(s)) return null;
    return s;
}

const IllustratedOptionCard: React.FC<IllustratedOptionCardProps> = ({
    inputKey,
    optionValue,
    optionLabel,
    selected,
    onClick,
    productVisualizationType,
    inlineInputValue,
    onInlineInputChange,
}) => {
    const Illustration = getIllustrationForOption(inputKey, optionValue, productVisualizationType);
    const showInlineInput = selected && typeof onInlineInputChange === 'function';
    // Realistic pre-generated preview images trump the abstract SVG
    // illustrations — same canonical subject rendered with each option
    // applied, so vendors see EXACTLY what the option does. Falls back
    // to the SVG illustration when no preview URL is on file yet.
    const previewUrl = getOptionPreviewUrl(inputKey, optionValue);
    // Some options can only be shown in motion — a camera move is
    // indistinguishable from any other in a frozen frame. Those get a short
    // muted loop instead of a still, with a poster for first paint.
    const previewVideoUrl = getOptionPreviewVideoUrl(inputKey, optionValue);
    const previewPosterUrl = getOptionPreviewPosterUrl(inputKey, optionValue);
    // Text-only card (no preview media, no SVG illustration) — e.g. Match
    // strength, room type. A corner check badge reads oddly on these, so we
    // show a small outlined-circle check before the label instead.
    const isTextOnly = !previewUrl && !previewVideoUrl && !Illustration;
    // "Custom" escape-hatch options (currently only environment =
    // custom). Render with a dashed border + pencil glyph so they stand
    // out from the preset cards, signaling "this is where you write your
    // own".
    const isCustomWritable = optionValue === 'custom';

    return (
        <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
                <motion.div
                    whileTap={{ scale: 0.98 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    // Selected = a firmer border + a check badge (below), never a
                    // heavy outline or a hard box-shadow ring.
                    className={cn(
                        'relative cursor-pointer overflow-hidden rounded-lg border-[1.5px] transition-colors',
                        isCustomWritable
                            ? cn('border-dashed', selected ? 'border-foreground bg-accent' : 'border-foreground/40 bg-muted/50')
                            : cn(selected ? 'border-foreground/30 bg-accent' : 'border-border bg-background'),
                    )}
                >
                    {selected && !isCustomWritable && !isTextOnly && (
                        <span className="absolute top-[5px] right-[5px] z-[2] flex size-[18px] items-center justify-center rounded-full bg-foreground text-background">
                            <Check className="size-3" strokeWidth={3.5} />
                        </span>
                    )}
                    {/* Visual area — prefer realistic preview, fall back to
                        abstract illustration. pointer-events: none so clicks
                        bubble up to the motion.div parent (avoids the aspect-
                        ratio card bug where an inner opacity-styled rect ate
                        clicks). */}
                    {previewVideoUrl ? (
                        <div className="pointer-events-none relative aspect-square w-full overflow-hidden border-b bg-muted">
                            {/* Muted autoplay loop — the only way to convey a camera
                                move. `poster` paints instantly so the card is never a
                                blank box while the clip buffers; playsInline keeps iOS
                                from hijacking it into fullscreen. */}
                            <video
                                src={previewVideoUrl}
                                poster={previewPosterUrl || undefined}
                                autoPlay
                                loop
                                muted
                                playsInline
                                preload="metadata"
                                draggable={false}
                                onContextMenu={(e) => e.preventDefault()}
                                className="pointer-events-none absolute inset-0 size-full object-cover select-none"
                            />
                        </div>
                    ) : previewUrl ? (
                        <div className="pointer-events-none relative aspect-square w-full overflow-hidden border-b bg-muted">
                            <img
                                src={previewUrl}
                                alt={optionLabel}
                                loading="lazy"
                                decoding="async"
                                draggable={false}
                                className="absolute inset-0 size-full object-cover select-none"
                            />
                        </div>
                    ) : Illustration ? (
                        <div className="pointer-events-none border-b bg-muted">
                            <Illustration />
                        </div>
                    ) : null}
                    {/* Label / inline input */}
                    {showInlineInput ? (
                        // When selected, the custom card becomes an inline text
                        // input. Clicking inside the input must NOT bubble up to
                        // the motion.div's onClick (which would re-fire the card
                        // click) — stopPropagation keeps focus.
                        <div className="flex items-center gap-0.5 px-1 py-1">
                            <Pencil className="size-2.5 shrink-0 text-foreground" />
                            <input
                                type="text"
                                value={inlineInputValue || ''}
                                placeholder="Type here…"
                                autoFocus={!inlineInputValue}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onChange={(e) => onInlineInputChange?.(e.target.value)}
                                className="min-w-0 flex-1 border-none bg-transparent text-center text-[11px] font-medium text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
                            />
                        </div>
                    ) : (
                        <div
                            className={cn(
                                'flex items-center justify-center gap-1 overflow-hidden px-1.5 py-1.5 text-center text-[11px] leading-snug font-medium text-ellipsis whitespace-nowrap',
                                isCustomWritable
                                    ? 'text-foreground'
                                    : (selected ? 'text-foreground' : 'text-muted-foreground'),
                            )}
                        >
                            {isCustomWritable && <Pencil className="size-2.5 shrink-0" />}
                            {selected && isTextOnly && !isCustomWritable && (
                                <span className="flex size-[13px] shrink-0 items-center justify-center rounded-full border border-foreground/40 text-foreground">
                                    <Check className="size-2" strokeWidth={3.5} />
                                </span>
                            )}
                            <span className="overflow-hidden text-ellipsis">
                                {optionLabel}
                            </span>
                        </div>
                    )}
                </motion.div>
            </TooltipTrigger>
            <TooltipContent side="top">{optionLabel}</TooltipContent>
        </Tooltip>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Illustration routing — maps inputKey × optionValue to a component
// ═══════════════════════════════════════════════════════════════════════

function getIllustrationRenderer(inputKey: string): boolean {
    return [
        'framing', 'backdrop', 'lighting',
        'aspectRatio', 'shadow', 'depthOfField', 'cameraHeight',
        'heroRotation', 'wallMaterial', 'floorMaterial', 'lightingStyle',
    ].includes(inputKey);
}

function getIllustrationForOption(
    inputKey: string,
    optionValue: string,
    productVisualizationType?: string,
): React.FC | null {
    switch (inputKey) {
        // roomType + style both render as plain text cards (no
        // illustrations) — vendors scan names faster than decoding
        // tiny stylized room/style icons, and it leaves the label
        // area free for the `custom` card's inline input affordance.
        case 'framing': {
            // For surface products, prefer the tile-subject variant
            // when one exists (studio-cover / catalog / macro-detail /
            // angled-hero have dedicated tile versions; overhead-flat-
            // lay and pedestal already use a tile in the main export).
            if (productVisualizationType === 'surface') {
                const SurfaceComp = FRAMING_ILLUSTRATIONS_SURFACE[optionValue];
                if (SurfaceComp) return SurfaceComp;
            }
            const Comp = FRAMING_ILLUSTRATIONS[optionValue];
            return Comp || null;
        }
        case 'backdrop': {
            return () => <BackdropSwatch slug={optionValue} />;
        }
        case 'lighting': {
            const Comp = LIGHTING_ILLUSTRATIONS[optionValue];
            return Comp || null;
        }
        case 'lightingStyle': {
            const Comp = LIGHTING_STYLE_ILLUSTRATIONS[optionValue];
            return Comp || null;
        }
        case 'wallMaterial': {
            const src = WALL_MATERIAL_TEXTURES[optionValue];
            return src ? () => <MaterialTextureSwatch src={src} /> : null;
        }
        case 'floorMaterial': {
            const src = FLOOR_MATERIAL_TEXTURES[optionValue];
            return src ? () => <MaterialTextureSwatch src={src} /> : null;
        }
        case 'aspectRatio': {
            return () => <AspectRatioVisual slug={optionValue} />;
        }
        case 'shadow': {
            const Comp = SHADOW_ILLUSTRATIONS[optionValue];
            return Comp || null;
        }
        case 'depthOfField': {
            const Comp = DOF_ILLUSTRATIONS[optionValue];
            return Comp || null;
        }
        case 'cameraHeight': {
            const Comp = CAMERA_HEIGHT_ILLUSTRATIONS[optionValue];
            return Comp || null;
        }
        case 'heroRotation': {
            const key = String(optionValue);
            if (productVisualizationType === 'surface') {
                const SurfaceComp = HERO_ROTATION_ILLUSTRATIONS_SURFACE[key];
                if (SurfaceComp) return SurfaceComp;
            }
            const Comp = HERO_ROTATION_ILLUSTRATIONS[key];
            return Comp || null;
        }
        default:
            return null;
    }
}

// ═══════════════════════════════════════════════════════════════════════
// Style Reference Section (for from-reference recipe)
// ═══════════════════════════════════════════════════════════════════════

interface DetectedRefObject { label: string; bbox?: number[]; }

interface StyleReferenceSectionProps {
    url?: string;
    uploading: boolean;
    /** Object detection in flight — shows an overlay on the image. */
    detecting?: boolean;
    /** Detected objects (label + bbox) — rendered as clickable chips. */
    detectedObjects?: DetectedRefObject[];
    /** Currently-selected object (to highlight the chip that matches targetObject). */
    selectedObject?: string;
    onUpload: (file: File) => Promise<void>;
    onRemove: () => void;
    /** Chip click — fills the targetObject input upstream. */
    onSelectObject?: (label: string) => void;
}

const StyleReferenceSection: React.FC<StyleReferenceSectionProps> = ({
    url,
    uploading,
    detecting,
    detectedObjects,
    selectedObject,
    onUpload,
    onRemove,
    onSelectObject,
}) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const [lightboxOpen, setLightboxOpen] = React.useState(false);
    // Hover state for the full-image bbox overlay + the loaded image's rendered
    // vs natural dims (to scale the pixel-space bbox onto the displayed image).
    const [hoveredLabel, setHoveredLabel] = React.useState<string | null>(null);
    const [imgBox, setImgBox] = React.useState<{ rw: number; rh: number; nw: number; nh: number } | null>(null);
    const objects: DetectedRefObject[] = Array.isArray(detectedObjects) ? detectedObjects : [];
    const hoveredObj = objects.find((o) => o.label === hoveredLabel);
    const measureImg = (img: HTMLImageElement) => {
        if (img?.naturalWidth) setImgBox({ rw: img.clientWidth, rh: img.clientHeight, nw: img.naturalWidth, nh: img.naturalHeight });
    };

    // ESC closes the lightbox — matches the GenerationLightbox UX.
    React.useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightboxOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [lightboxOpen]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploading(true);
        try {
            await onUpload(file);
        } catch {
            // ignore
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    /** The detected-object chip. Rendered inline on the card and again over
     *  the lightbox, where it needs a translucent ground to stay legible. */
    const ObjectChip: React.FC<{ label: string; onDark?: boolean; onMouseEnter?: () => void; onMouseLeave?: () => void; onClick: (e: React.MouseEvent) => void }> = ({ label, onDark, onMouseEnter, onMouseLeave, onClick }) => {
        const isSelected = (selectedObject || '').toLowerCase().trim() === label.toLowerCase().trim();
        return (
            <button
                type="button"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onClick}
                className={cn(
                    'rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
                    onDark
                        ? cn('text-white backdrop-blur-sm',
                             isSelected ? 'border-white bg-white/25' : 'border-white/35 bg-white/10 hover:bg-white/20')
                        : cn('text-foreground',
                             isSelected ? 'border-foreground/30 bg-accent hover:bg-accent/80' : 'border-border bg-background hover:border-foreground/30 hover:bg-muted'),
                )}
            >
                {onDark ? label : label.toLowerCase()}
            </button>
        );
    };

    return (
        <div className="border-b px-4 py-3">
            {url ? (
                <div className="relative">
                    <div
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); setLightboxOpen(true); }}
                        title="Click to open full size"
                        className="aspect-[16/10] w-full cursor-zoom-in rounded-lg border border-border bg-muted bg-cover bg-center transition-[filter] hover:brightness-95"
                        style={{ backgroundImage: `url(${url})` }}
                    />
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRemove(); }}
                        aria-label="Remove reference image"
                        className="absolute top-1.5 right-1.5 flex size-[22px] items-center justify-center rounded-full border-none bg-black/50 text-white transition-colors hover:bg-destructive/80"
                    >
                        <X className="size-3" />
                    </button>
                    {detecting && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 bg-black/45 text-white">
                            <Loader2 className="size-4 animate-spin" />
                            <span className="text-xs font-medium">Detecting objects…</span>
                        </div>
                    )}
                    {lightboxOpen && url && ReactDOM.createPortal(
                        <div
                            onClick={() => setLightboxOpen(false)}
                            className="fixed inset-0 z-[9999] flex cursor-zoom-out flex-col items-center justify-center gap-4 bg-black/75 p-12 backdrop-blur-md"
                        >
                            {/* Image + hover bbox overlay. The wrapper hugs the
                                <img> so the absolute overlay lines up exactly. */}
                            <div
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                className="relative inline-block cursor-default leading-none"
                            >
                                <img
                                    src={url}
                                    alt="Style reference"
                                    onLoad={(e) => measureImg(e.currentTarget)}
                                    onContextMenu={(e) => e.preventDefault()}
                                    onDragStart={(e) => e.preventDefault()}
                                    className="block h-auto max-h-[78vh] w-auto max-w-[90vw] shadow-2xl select-none"
                                />
                                {hoveredObj && Array.isArray(hoveredObj.bbox) && hoveredObj.bbox.length === 4 && imgBox && (() => {
                                    const [x1, y1, x2, y2] = hoveredObj.bbox as number[];
                                    const sx = imgBox.rw / imgBox.nw;
                                    const sy = imgBox.rh / imgBox.nh;
                                    return (
                                        <div
                                            className="pointer-events-none absolute border-2 border-white"
                                            style={{
                                                left: x1 * sx,
                                                top: y1 * sy,
                                                width: Math.max(2, (x2 - x1) * sx),
                                                height: Math.max(2, (y2 - y1) * sy),
                                            }}
                                        >
                                            <span className="absolute -top-5 -left-0.5 bg-white px-1.5 text-[11px] leading-snug font-medium whitespace-nowrap text-black">
                                                {hoveredObj.label}
                                            </span>
                                        </div>
                                    );
                                })()}
                            </div>
                            {/* Detected object chips — hover to see the bounding box. */}
                            {objects.length > 0 && (
                                <div
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    className="flex max-w-[90vw] cursor-default flex-wrap justify-center gap-1.5"
                                >
                                    {objects.map((o) => (
                                        <ObjectChip
                                            key={o.label}
                                            label={o.label}
                                            onDark
                                            onMouseEnter={() => setHoveredLabel(o.label)}
                                            onMouseLeave={() => setHoveredLabel(null)}
                                            onClick={(e) => { e.stopPropagation(); onSelectObject?.(o.label); }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>,
                        document.body,
                    )}
                </div>
            ) : (
                <div
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    className="flex aspect-[16/10] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-border bg-muted transition-colors hover:border-foreground/30 hover:bg-accent"
                >
                    {isUploading ? (
                        <span className="text-xs text-muted-foreground">Uploading…</span>
                    ) : (
                        <>
                            <Upload className="size-5 text-muted-foreground" />
                            <span className="px-2 text-center text-xs text-muted-foreground">
                                Upload a reference image
                            </span>
                        </>
                    )}
                </div>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            {/* Detected objects — clickable chips so the vendor picks the target
                object instead of typing it. (The "Detecting…" state shows as an
                overlay on the image above.) Open the image to preview each box. */}
            {url && !detecting && objects.length > 0 && (
                <div className="mt-2">
                    <p className="mb-1.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                        Detected objects · click to use
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {objects.map((o) => (
                            <ObjectChip
                                key={o.label}
                                label={o.label}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectObject?.(o.label);
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Mode selector — segmented 3-way toggle for recipes that use the
// options / ai / preset mode system. Rendered above the accordion inputs.
// ═══════════════════════════════════════════════════════════════════════

interface ModeTab {
    value: string;
    label: string;
    hint: string;
}

interface ModeSelectorProps {
    mode: string;
    onChange: (mode: string) => void;
    tabs: ModeTab[];
    // When a toolbar already sits directly above (e.g. the surface shot-type
    // row), tighten the top gap so the tabs don't float too far down.
    dense?: boolean;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ mode, onChange, tabs, dense }) => (
    // Breathing room at the top of the card (the header row is gone).
    <div className={cn('flex gap-1 px-4 pb-2', dense ? 'pt-1.5' : 'pt-4')}>
        {tabs.map((tab) => {
            const active = tab.value === mode;
            const isAi = tab.value === 'ai';
            return (
                <button
                    key={tab.value}
                    type="button"
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (!active) onChange(tab.value);
                    }}
                    title={tab.hint}
                    aria-pressed={active}
                    className={cn(
                        'inline-flex flex-1 items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-[11px] font-medium transition-colors',
                        active && isAi
                            ? 'cursor-default border-foreground bg-foreground text-background'
                            : active
                            ? 'cursor-default border-foreground/30 bg-accent text-foreground'
                            : 'cursor-pointer border-border bg-transparent text-muted-foreground hover:border-foreground/30 hover:bg-muted',
                    )}
                >
                    {isAi && <Sparkles className="size-3" />}
                    {tab.label}
                </button>
            );
        })}
    </div>
);
